{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2019 - 2021                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Utils;

interface

uses
  Types, Web, SysUtils, Math, Classes, JS;

const
  DayMonday = 1;
  DayTuesday = 2;
  DayWednesday = 3;
  DayThursday = 4;
  DayFriday = 5;
  DaySaturday = 6;
  DaySunday = 7;

  MonthJanuary = 1;
  MonthFebruary = 2;
  MonthMarch = 3;
  MonthApril = 4;
  MonthMay = 5;
  MonthJune = 6;
  MonthJuly = 7;
  MonthAugust = 8;
  MonthSeptember = 9;
  MonthOctober = 10;
  MonthNovember = 11;
  MonthDecember = 12;

type
  TInterfaceList = class;

  Int64 = NativeInt {unimplemented}; // only 53 bits at runtime

  TProc = reference to procedure;
  TProcBoolean = reference to procedure(AValue: Boolean);
  TProcInteger = reference to procedure(AValue: Integer);
  TProcString = Types.TProcString;

  EConvertError = class(Exception);

  EEncodingError = class(Exception);

  EStreamError = class(Exception);
  EStringStreamError = class(EStreamError);
  EFilerError = class(EStreamError);
  EReadError = class(EFilerError);
  EWriteError = class(EFilerError);

  TBytesHelper = record helper for TBytes
  strict private
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    function Compare(const ARight: TBytes): Integer;
    function High: Integer;
    function Low: Integer;
    function SubBytes(const AStartIndex: Integer): TBytes; overload; inline;
    function SubBytes(const AStartIndex, ALength: Integer): TBytes; overload; inline;
    property Length: Integer read get_Length write set_Length;
  end;

  TWords = array of Word;
  TWordsHelper = record helper for TWords
  strict private
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    class function FromBytes(const ABytes: TBytes): TWords; static;
    function High: Integer;
    function Low: Integer;
    function ToBytes: TBytes;
    property Length: Integer read get_Length write set_Length;
  end;

  TCharHelper = record helper for Char
  public const
    Length = 1;
    Size = 2;
  public
    function IsDigit: Boolean;
    function ToOrd: Integer;
    function ToUpper: Char; overload;
    class function ToUpper(const AValue: Char): Char; overload; assembler; static;
  end;

  TIntegerHelper = record helper for Integer
  public
    function Equals(const AValue: Integer): Boolean; inline;
    function NotEquals(const AValue: Integer): Boolean; inline;
    class function Parse(const AValue: string): Integer; static;
    function ToString: string; overload;
    class function ToString(const AValue: Integer): string; overload; assembler; static;
    class function TryParse(const AString: string; out AValue: Integer): Boolean; static; inline;
  end;

  TLongIntHelper = record helper for LongInt
  public
    function Equals(const AValue: LongInt): Boolean; inline;
    function NotEquals(const AValue: LongInt): Boolean; inline;
    class function Parse(const AValue: string): Integer; static;
    function ToString: string; overload;
    class function ToString(const AValue: LongInt): string; overload; assembler; static;
    class function TryParse(const AString: string; out AValue: Integer): Boolean; static; inline;
  end;

  TWordHelper = record helper for Word
  public
    class function FromBytes(const ALow, AHigh: Byte): Word; static;
    procedure ToBytes(out ALow, AHigh: Byte);
  end;

  TStringHelper = record helper for string
  strict private
    function get_Chars(const AIndex: Integer): Char; inline;
    function get_Length: Integer; inline;
    function Pos(const ASubStr, AStr: string; const AOffset: Integer): Integer;
    function Reverse: string;
    procedure set_Chars(const AIndex: Integer; const AValue: Char); inline;
    procedure set_Length(const AValue: Integer); inline;
  public const
    Empty = '';
  public
    function CompareTo(const AValue: string): Integer; overload;
    class function CompareTo(const ALeft, ARight: string): Integer; overload; assembler; static;
    function Contains(const AValue: string): Boolean;
    function Equals(const AValue: string): Boolean;
    function FirstChar: Char;
    function Format(const AArgs: array of JSValue): string; overload;
    class function Format(const AValue: string; const AArgs: array of JSValue): string; overload; static;
    function High: Integer; overload; inline;
    function IndexOf(const AValue: Char): Integer; overload; inline;
    function IndexOf(const AValue: string; const AStartIndex, ACount: Integer): Integer; overload;
    function IndexOf(const AValue: string): Integer; overload; inline;
    function Insert(const AStartIndex: Integer; const AValue: string): string;
    function IsEmpty: Boolean; inline;
    function IsNumber: Boolean;
    function LastIndexOf(const AValue: Char): Integer; overload; inline;
    class function Low: Integer; static; inline;
    function NotEquals(const AValue: string): Boolean;
    function NotIsEmpty: Boolean; inline;
    function PadLeft(const ATotalWidth: Integer): string; overload; inline;
    function PadLeft(ATotalWidth: Integer; const APaddingChar: Char): string; overload; inline;
    function Remove(const AStartIndex: Integer): string; overload; inline;
    function Remove(const AStartIndex, ACount: Integer): string; overload; inline;
    function Replace(const AOldValue: string; const ANewValue: string): string; overload;
    function Same(const AValue: string): Boolean; inline;
    class function StartsText(const ASubText, AText: string): Boolean; static;
    function StartsWith(const AValue: string): Boolean; overload; inline;
    function StartsWith(const AValue: string; AIgnoreCase: Boolean): Boolean; overload;
    function Substring(const AStartIndex: Integer): string; overload; inline;
    function Substring(const AStartIndex, ALength: Integer): string; overload; inline;
    function ToLower: string;
    function ToUpper: string;
    function Trim: string;
    property Chars[const AIndex: Integer]: Char read get_Chars write set_Chars;
    property Length: Integer read get_Length write set_Length;
  end;

  TDateTimeHelper = record helper for TDateTime
  public
    function Equals(const AValue: TDateTime): Boolean; inline;
    function NotEquals(const AValue: TDateTime): Boolean; inline;
    function ToString: string; inline;
  end;

  TMonthNameArray = array[1..12] of string;
  TMonthNames = TMonthNameArray;
  TDayNames = array[1..7] of string;
  TFormatSettings = record
  strict private
    class function GetBrowserLocale: string; assembler; static;
    class function GetLocaleShortDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleDecimalSeparator(const ALocale: string): string; assembler; static;
    class function GetLocaleLongMonthName(const AMonth: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleLongDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleShortMonthName(const AMonth: Integer; const ALocale: string): string; assembler; static;
    class function GetLocaleLongTimeFormat(ALocale: string): string; static;
    class function GetLocaleShortTimeFormat(ALocale: string): string; static;
    class function GetLocaleShortDateFormat(ALocale: string): string; static;
  public
    CurrencyDecimals: Byte;
    CurrencyFormat: Byte;
    CurrencyString: string;
    DateSeparator: Char;
    DateTimeToStrFormat: array[Boolean] of string;
    DecimalSeparator: string;
    LongDateFormat: string;
    LongDayNames: TDayNames;
    LongMonthNames: TMonthNames;
    LongTimeFormat: string;
    NegCurrFormat: Byte;
    ShortDateFormat: string;
    ShortDayNames: TDayNames;
    ShortMonthNames: TMonthNames;
    ShortTimeFormat: string;
    ThousandSeparator: string;
    TimeAMString: string;
    TimePMString: string;
    TimeSeparator: Char;
    TwoDigitYearCenturyWindow: Word;
  public
    class function Create: TFormatSettings; overload; static;
    class function Create(const ALocale: string): TFormatSettings; overload; static;
    class function Invariant: TFormatSettings; static;
  end;


  TTMSEncoding = class(TObject)
  strict private class var
    FANSI: TTMSEncoding;
    FUnicode: TTMSEncoding;
  strict private
    class function get_ANSI: TTMSEncoding; static;
    class function get_Default: TTMSEncoding; static;
    class function get_Unicode: TTMSEncoding; static;
  public
    function GetByteCount(const AChars: string): Integer; overload; virtual; abstract;
    function GetByteCount(const AChars: string; const ACharCount: Integer): Integer; overload;
    function GetByteCount(const AChars: string; const ACharIndex, ACharCount: Integer): Integer; overload; inline;
    function GetBytes(const AChars: string): TBytes; overload; virtual; abstract;
    function GetBytes(const AChars: string; const ACharCount: Integer): TBytes; overload;
    function GetBytes(const AChars: string; const ACharIndex, ACharCount: Integer): TBytes; overload; inline;
    function GetString(const ABytes: TBytes): string; overload; virtual; abstract;
    function GetString(const ABytes: TBytes; const ACharCount: Integer): string; overload;
    function GetString(const ABytes: TBytes; const ACharIndex, ACharCount: Integer): string; overload; inline;
    class function IsStandardEncoding(AEncoding: TTMSEncoding): Boolean; static;
    class property ANSI: TTMSEncoding read get_ANSI;
    class property Default: TTMSEncoding read get_Default;
    class property Unicode: TTMSEncoding read get_Unicode;
  end;

  TANSIEncoding = class(TTMSEncoding)
  public
    function GetByteCount(const AChars: string): Integer; overload; override;
    function GetBytes(const AChars: string): TBytes; overload; override;
    function GetString(const ABytes: TBytes): string; overload; override;
  end;

  TUnicodeEncoding = class(TTMSEncoding)
  strict private
    procedure InternalGetBytes(const AChars: string; out ABytes: TBytes);
  public
    function GetByteCount(const AChars: string): Integer; overload; override;
    function GetBytes(const AChars: string): TBytes; overload; override;
    function GetString(const ABytes: TBytes): string; overload; override;
  end;


  TStreamHelper = class helper for TStream
  public
    function CopyFrom(const ASource: TStream; ACount: Int64): Int64;
  end;

  TTMSStringStream = class(TBytesStream)
  private
    FEncoding: TTMSEncoding;
    FFileName: string;
    FOwnsEncoding: Boolean;
    FError: TProcString;
    FLoaded: TProc;
    FRequest: TJSXMLHTTPRequest;
    function GetDataString: string;
    function InternalLoadFromFileP(const AFileName: string): TJSPromise;
    function OnAbort(AEvent: TEventListenerEvent): Boolean;
    function OnError(AEvent: TEventListenerEvent): Boolean;
    function OnLoad(AEvent: TEventListenerEvent): Boolean;
  public
    constructor Create; overload;
    constructor Create(const AString: string); overload;
    constructor Create(const AString: string; AEncoding: TTMSEncoding; AOwnsEncoding: Boolean = True); overload;
    destructor Destroy; override;
    procedure LoadFromFile(const AFileName: string); overload;
    procedure LoadFromFile(const AFileName: string; const ALoaded: TProc); overload;
    procedure LoadFromFile(const AFileName: string; const ALoaded: TProc; const AError: TProcString); overload;
    function LoadFromFileP(const AFileName: string): Boolean; async;
    function ReadString(ACount: Integer): string;
    procedure WriteString(const AString: string);
    property DataString: string read GetDataString;
  end;

  IInterfaceList = interface
  ['{285DEA8A-B865-11D1-AAA7-00C04FB17A72}']
    function Add(const AItem: IInterface): Integer;
    procedure Clear;
    procedure Delete(const AIndex: Integer);
    procedure Exchange(const AIndex1, AIndex2: Integer);
    function Expand: TInterfaceList;
    function First: IInterface;
    function get_Capacity: Integer;
    function get_Count: Integer;
    function get_Items(const AIndex: Integer): IInterface;
    function IndexOf(const AItem: IInterface): Integer;
    function IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure Insert(const AIndex: Integer; const AItem: IInterface);
    function Last: IInterface;
    procedure Lock;
    function Remove(const AItem: IInterface): Integer;
    function RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure set_Capacity(const AValue: Integer);
    procedure set_Count(const AValue: Integer);
    procedure set_Items(const AIndex: Integer; const AItem: IInterface);
    procedure Unlock;
    property Count: Integer read get_Count write set_Count;
    property Items[const AIndex: Integer]: IInterface read get_Items write set_Items; default;
  end;

  TInterfaceListEnumerator = class(TObject)
  strict private
    FIndex: Integer;
    FInterfaceList: TInterfaceList;
    function get_Current: IInterface; inline;
  public
    constructor Create(const AInterfaceList: TInterfaceList);
    function MoveNext: Boolean;
    property Current: IInterface read get_Current;
  end;

  IInterfaceListEx = interface(IInterfaceList)
    ['{FDB39D70-65B9-4995-9436-6084ACA05DB3}']
    function GetEnumerator: TInterfaceListEnumerator;
  end;

  TCompareItems = reference to function(const ALeft, ARight: IInterface): TValueRelationship;

  IInterfaceListEx2 = interface(IInterfaceList)
    ['{4FE71218-0F8F-4027-B050-5E84E2202604}']
    procedure Sort(const ACompare: TCompareItems);
  end;

  TInterfaceList = class(TInterfacedObject, IInterfaceList, IInterfaceListEx, IInterfaceListEx2)
  strict private
    FList: TFPList;
  strict protected
    function Add(const AItem: IInterface): Integer;
    procedure Clear;
    procedure Delete(const AIndex: Integer);
    procedure Exchange(const AIndex1, AIndex2: Integer);
    function Expand: TInterfaceList;
    function First: IInterface;
    function GetEnumerator: TInterfaceListEnumerator;
    function get_Capacity: Integer;
    function get_Count: Integer;
    function get_Items(const AIndex: Integer): IInterface;
    function IndexOf(const AItem: IInterface): Integer;
    function IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure Insert(const AIndex: Integer; const AItem: IInterface);
    function Last: IInterface;
    procedure Lock;
    function Remove(const AItem: IInterface): Integer;
    function RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure set_Capacity(const AValue: Integer);
    procedure set_Count(const AValue: Integer);
    procedure set_Items(const AIndex: Integer; const AItem: IInterface);
    procedure Sort(const ACompare: TCompareItems);
    procedure Unlock;
    property Capacity: Integer read get_Capacity write set_Capacity;
    property Items[const AIndex: Integer]: IInterface read get_Items write set_Items; default;
  protected
    property Count: Integer read get_Count write set_Count;
  public
    constructor Create;
    destructor Destroy; override;
  end;

  TPath = record
  public
    class function Combine(const APath1, APath2: string): string; static;
    class function GetDirectoryName(const AFileName: string): string; static;
    class function GetExtension(const AFileName: string): string; static;
  end;

  TDirectory = record
  public
    class function GetCurrentDirectory: string; static;
  end;

  TStringBuilder = class(TObject)
  strict private
    FData: string;
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    constructor Create(const AValue: string); overload;
    function Append(const AValue: Integer): TStringBuilder; overload;
    function Append(const AValue: string): TStringBuilder; overload;
    function Insert(const AIndex: Integer; const AValue: Integer): TStringBuilder; overload;
    function Insert(const AIndex: Integer; const AValue: string): TStringBuilder; overload;
    function ToString: string; overload; override;
    property Length: Integer read get_Length write set_Length;
  end;

function DateTimeToStr(const ADateTime: TDateTime; const AForceTimeIfZero: Boolean = False): string; overload;
function DateTimeToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings; const AForceTimeIfZero: Boolean = False): string; overload;

function DateToStr(const ADateTime: TDateTime): string; overload;
function DateToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings): string; overload;

function FormatDateTime(const AFormatStr: string; const ADateTime: TDateTime): string; overload;
function FormatDateTime(const AFormatStr: string; const ADateTime: TDateTime; const AFormatSettings: TFormatSettings): string; overload;

function TimeToStr(const ATime: TDateTime): string; overload;
function TimeToStr(const ATime: TDateTime; const AFormatSettings: TFormatSettings): string; overload;

function TryStrToDate(const AString: string; out AValue: TDateTime): Boolean; overload;
function TryStrToDate(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean; overload;

function StrToDateDef(const AValue: string; const ADefault: TDateTime): TDateTime; overload;
function StrToDateDef(const AValue: string; const ADefault: TDateTime; const AFormatSettings: TFormatSettings): TDateTime; overload;

function StrToDate(const AValue: string): TDateTime; overload;
function StrToDate(const AValue: string; const AFormatSettings: TFormatSettings): TDateTime; overload;

function StrToDateTimeDef(const AValue: string; const ADefault: TDateTime): TDateTime; overload;
function StrToDateTimeDef(const AValue: string; const ADefault: TDateTime; const AFormatSettings: TFormatSettings): TDateTime; overload;

function StrToDateTime(const AString: string): TDateTime; overload;
function StrToDateTime(const AString: string; const AFormatSettings: TFormatSettings): TDateTime; overload;

function TryStrToDateTime(const AString: string; out AValue: TDateTime): Boolean; overload;
function TryStrToDateTime(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean; overload;

function TryStrToFloat(const AString: string; out AValue: Extended): Boolean; overload;
function TryStrToFloat(const AString: string; out AValue: Extended; const AFormatSettings: TFormatSettings): Boolean; overload;

function TryStrToFloat(const AString: string; out AValue: Double): Boolean; overload;
function TryStrToFloat(const AString: string; out AValue: Double; const AFormatSettings: TFormatSettings): Boolean; overload;

function TryStrToTime(const AString: string; out AValue: TDateTime): Boolean; overload;
function TryStrToTime(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean; overload;

function StrToFloat(const AString: string): Double; overload;
function StrToFloat(const AString: string; const AFormatSettings: TFormatSettings): Double; overload;

function StrToTime(const AString: string): TDateTime; overload;
function StrToTime(const AString: string; const AFormatSettings: TFormatSettings): TDateTime; overload;

function StrToTimeDef(const AString: string; const ADefault: TDateTime): TDateTime; overload;
function StrToTimeDef(const AString: string; const ADefault: TDateTime; const AFormatSettings: TFormatSettings): TDateTime; overload;

procedure UseVariable(const AValue: IInterface); overload;
procedure UseVariable(const AValue: Integer); overload;
procedure UseVariable(const AValue: string); overload;

function FloatToStrF(const AValue: Double; const AFormat: TFloatFormat; const APrecision, ADigits: Integer): string; overload;
function FloatToStrF(const AValue: Double; const AFormat: TFloatFormat; const APrecision, ADigits: Integer; const AFormatSettings: TFormatSettings): string; overload;

function FloatToStr(const AValue: Double): string; overload;
function FloatToStr(const AValue: Double; const AFormatSettings: TFormatSettings): string; overload;

function Format(const AFmt: string; const AArgs: array of JSValue): string; overload;
function Format(const AFmt: string; const AArgs: array of JSValue; const AFormatSettings: TFormatSettings): string; overload;

function FormatFloat(const AFormat: string; const AValue: Double): string; overload;
function FormatFloat(const AFormat: string; const AValue: Double; const AFormatSettings: TFormatSettings): string; overload;

function ArrayBufferToBase64(Buffer: TJSArrayBuffer): string;
function Base64ToArrayBuffer(Base64: string): TJSArrayBuffer;
function StringToBase64(AValue: string): string;
function Base64ToString(AValue: string): string;

function SizeOf(const AValue: Double): Integer; overload;
function SizeOf(const AValue: Integer): Integer; overload;

procedure InsertAfter(newNode, referenceNode: TJSElement);

var
  FormatSettings: TFormatSettings;

implementation

uses
  RTLConsts, WEBLib.Consts;

const
  MaxDigits = 15;

  feInvalidFormat   = 1;
  feMissingArgument = 2;
  feInvalidArgIndex = 3;

var
  FCompare: TCompareItems;

type
  TFormatDateTimeHelper = record
  strict private
    class procedure StoreFormat(var AResult: string; var AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek: Word; const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime; ANesting: Integer; ATimeFlag: Boolean); static;
    class procedure StoreInt(var AResult: string; const AValue, ADigits: Integer); static;
    class procedure StoreStr(var AResult: string; const AFormatStr: string; const APos, ALength: Integer); static;
    class procedure StoreString(var AResult: string; const AString: string); static;
  public
    class function FormatDateTime(const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime): string; static;
  end;

function DateTimeToStr(const ADateTime: TDateTime; const AForceTimeIfZero: Boolean = False): string;
begin
  Result := DateTimeToStr(ADateTime, FormatSettings, AForceTimeIfZero);
end;

function DateTimeToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings; const AForceTimeIfZero: Boolean = False): string;
begin
  Result := FormatDateTime(AFormatSettings.DateTimeToStrFormat[AForceTimeIfZero], ADateTime, AFormatSettings);
end;

function DateToStr(const ADateTime: TDateTime): string;
begin
  Result := DateToStr(ADateTime, FormatSettings);
end;

function DateToStr(const ADateTime: TDateTime; const AFormatSettings: TFormatSettings): string;
const
  cDateFormat = 'ddddd';
begin
  Result := FormatDateTime(cDateFormat, ADateTime, AFormatSettings);
end;

function FormatDateTime(const AFormatStr: string; const ADateTime: TDateTime): string;
begin
  Result := FormatDateTime(AFormatStr, ADateTime, FormatSettings);
end;

function FormatDateTime(const AFormatStr: string; const ADateTime: TDateTime; const AFormatSettings: TFormatSettings): string;
begin
  Result := TFormatDateTimeHelper.FormatDateTime(AFormatStr, AFormatSettings, ADateTime);
end;

function TimeToStr(const ATime: TDateTime): string;
begin
  Result := TimeToStr(ATime, FormatSettings);
end;

function TimeToStr(const ATime: TDateTime; const AFormatSettings: TFormatSettings): string;
begin
  Result := FormatDateTime('tt', ATime, AFormatSettings);
end;

function IntStrToDate(out AErrorMsg : string; const AString: string; const AUseFormat: string; const AFormatSettings: TFormatSettings): TDateTime;
const
  cWhiteSpace = ' '#8#9#10#12#13;
  cDigits = '0123456789';

  procedure FixErrorMsg(const AErrMarg : string);
  begin
    AErrorMsg := Format(SInvalidDateFormat, [AErrMarg]);
  end;

var
  lCode: Integer;
  lCount: Longint;
  lDateFormat: string;
  lDay: Word;
  lDayPos: Byte;
  lDayValues: Word;
  lLen: Longint;
  lMonth: Word;
  lMonthPos: Byte;
  lMonthValues: Word;
  lNumSep: Longint;
  lS1: string;
  lSeparator: Char;
  lValues: array of Integer;
  lWhich: Byte;
  lYear: Word;
  lYearMoreThenTwoDigits: Boolean;
  lYearPos: Byte;
  lYearValues: Word;
begin
  SetLength(lValues, 4);
  Result := 0;
  lLen := Length(AString);
  AErrorMsg := '';
  while (lLen > 0) and (Pos(AString[lLen], cWhiteSpace) > 0) do
    Dec(lLen);

  if lLen = 0 then
  begin
    FixErrorMsg(AString);
    Exit;
  end;

  lYearMoreThenTwoDigits := False;
  lSeparator := AFormatSettings.DateSeparator;
  if lSeparator = #0 then
    lSeparator := '-';
  // Writeln('Separator: ', lSeparator);

  lDateFormat := UpperCase(AUseFormat);
  { Determine order of lDayValues,lMonthValues,lYearValues }
  lYearPos := 0;
  lMonthPos := 0;
  lDayPos := 0;
  lWhich := 0;
  lCount := 0;
  while (lCount < Length(lDateFormat)) and (lWhich < 3) do
  begin
    Inc(lCount);
    case lDateFormat[lCount] of
      'Y':
      if lYearPos = 0 then
      begin
        Inc(lWhich);
        lYearPos := lWhich;
      end;
      'M':
      if lMonthPos = 0 then
      begin
        Inc(lWhich);
        lMonthPos := lWhich;
      end;
      'D':
      if lDayPos = 0 then
      begin
        Inc(lWhich);
        lDayPos := lWhich;
      end;
    end;
  end;
  // Writeln('YP: ',lYearPos,', MP: ',lMonthPos,', DP: ',lDayPos);

  for lCount := 1 to 3 do
    lValues[lCount] := 0;
  lS1 := '';
  lNumSep := 0;
  for lCount := 1 to lLen do
  begin
    if Pos(AString[lCount], cDigits) > 0 then
      lS1 := lS1 + AString[lCount];
     { space can be part of the shortdateformat, and is defaultly in slovak
       windows, therefor it shouldn't be taken as separator (unless so specified)
       and ignored }

    if (lSeparator <> ' ') and (AString[lCount] = ' ') then
      Continue;
    if (AString[lCount] = lSeparator) or ((lCount = lLen) and (Pos(AString[lCount], cDigits) > 0)) then
    begin
      Inc(lNumSep);
      if lNumSep > 3 then
      begin
//        Writeln('More than 3 separators');
        FixErrorMsg(AString);
        Exit;
      end;

         // Check if the year has more then two Digits (if lNumSep = lYearPos, then we are evaluating the year.)
      if (lNumSep = lYearPos) and (Length(lS1) > 2) then
        lYearMoreThenTwoDigits := True;
      Val(lS1, lValues[lNumSep], lCode);
      if lCode <> 0 then
      begin
//        Writeln('S1 not a number ',lS1);
        FixErrorMsg(AString);
        Exit;
      end;
      lS1 := '';
    end
    else if Pos(AString[lCount], cDigits) = 0 then
    begin
//      Writeln('Not a number at pos ',lCount,' ',AString[lCount]);
      FixErrorMsg(AString);
      Exit;
    end;
  end;
//  Writeln('Which : ',lWhich,' N : ',lNumSep);
  if (lWhich < 3) and (lNumSep > lWhich) then
  begin
    FixErrorMsg(AString);
    Exit;
  end;

  // Fill in lValues.
  DecodeDate(Date, lYear, lMonth, lDay);
  if lNumSep = 3 then
  begin
    lYearValues := lValues[lYearPos];
    lMonthValues := lValues[lMonthPos];
    lDayValues := lValues[lDayPos];
  end
  else
  begin
    lYearValues := lYear;
    if lNumSep < 2 then
    begin
      lDayValues := lValues[1];
      lMonthValues := lMonth;
    end
    else if lDayPos < lMonthPos then
    begin
      lDayValues := lValues[1];
      lMonthValues := lValues[2];
    end
    else
    begin
      lDayValues := lValues[2];
      lMonthValues := lValues[1];
    end;
  end;
  if (lYearValues >= 0) and (lYearValues < 100) and not lYearMoreThenTwoDigits then
  begin
    lYear := lYear - AFormatSettings.TwoDigitYearCenturyWindow;
    Inc(lYearValues, lYear div 100 * 100);
    if (AFormatSettings.TwoDigitYearCenturyWindow > 0) and (lYearValues < lYear) then
      Inc(lYearValues, 100);
  end;

  if not TryEncodeDate(lYearValues, lMonthValues, lDayValues, Result) then
    AErrorMsg := SErrInvalidDate;
end;

function TryStrToDate(const AString: string; out AValue: TDateTime): Boolean;
begin
  Result := TryStrToDate(AString, AValue, FormatSettings);
end;

function TryStrToDate(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean;
var
  lMsg: string;
begin
  Result := AString.NotIsEmpty;
  if Result then
  begin
    AValue := IntStrToDate(lMsg, AString, AFormatSettings.ShortDateFormat, AFormatSettings);
    Result := lMsg.IsEmpty;
  end
  else
    AValue := 0;
end;

function StrToDateDef(const AValue: string; const ADefault: TDateTime): TDateTime;
begin
  Result := StrToDateDef(AValue, ADefault, FormatSettings);
end;

function StrToDateDef(const AValue: string; const ADefault: TDateTime; const AFormatSettings: TFormatSettings): TDateTime;
begin
  if not TryStrToDate(AValue, Result, AFormatSettings) Then
    Result := ADefault;
end;

function StrToDate(const AValue: string): TDateTime;
begin
  Result := StrToDate(AValue, FormatSettings);
end;

function StrToDate(const AValue: string; const AFormatSettings: TFormatSettings): TDateTime;
var
  lMsg : string;
begin
  Result := IntStrToDate(lMsg, AValue, AFormatSettings.ShortDateFormat, AFormatSettings);
  if lMsg <> '' then
    raise EConvertError.Create(lMsg);
end;

function StrToDateTimeDef(const AValue: string; const ADefault: TDateTime): TDateTime;
begin
  Result := StrToDateTimeDef(AValue, ADefault, FormatSettings);
end;

function StrToDateTimeDef(const AValue: string; const ADefault: TDateTime; const AFormatSettings: TFormatSettings): TDateTime;
begin
  if not TryStrToDateTime(AValue, Result, AFormatSettings) then
    Result := ADefault;
end;

function SplitDateTimeStr(ADateTimeStr: string; out ADateStr, ATimeStr: string; const AFormatSettings: TFormatSettings): Integer;

{ Helper function for StrToDateTime
  Pre-condition
  Date is before Time
  If either Date or Time is omitted then see what fits best, a time or a date (issue #0020522)
  Date and Time are separated by whitespace (space Tab, Linefeed or carriage return)
  FS.DateSeparator can be the same as FS.TimeSeparator (issue #0020522)
  If they are both #32 and TrimWhite(DateTimeStr) contains a #32 a date is assumed.
  Post-condition
  DateStr holds date as string or is empty
  TimeStr holds time as string or is empty
  Result = number of strings returned, 0 = error
}
const
  cWhiteSpace = #9#10#13#32;

var
  lPos: Integer;
  lDummyDT: TDateTime;
begin
  Result := 0;
  ADateStr := '';
  ATimeStr := '';
  ADateTimeStr := Trim(ADateTimeStr);
  if Length(ADateTimeStr) = 0 then
    Exit;
  if (AFormatSettings.DateSeparator = #32) and (AFormatSettings.TimeSeparator = #32) and
    (Pos(#32, ADateTimeStr) > 0) then
  begin
    ADateStr := ADateTimeStr;
    {
      Assume a date: dd [mm [yy]].
      Really fancy would be counting the number of cWhiteSpace occurrences and decide
      and split accordingly
    }
    Exit(1);
  end;
  lPos := 1;
  // find separator
  if (AFormatSettings.DateSeparator <> #32) then
  begin
    while (lPos < Length(ADateTimeStr)) and (not(Pos(ADateTimeStr[lPos + 1], cWhiteSpace) > 0)) do
      Inc(lPos);
  end
  else
  begin
    lPos := Pos(AFormatSettings.TimeSeparator, ADateTimeStr);
    if (lPos <> 0) then
      repeat
        Dec(lPos);
      until (lPos = 0) or (Pos(ADateTimeStr[lPos], cWhiteSpace) > 0);
  end;
  // Always fill ADateStr, it eases the algorithm later
  if (lPos = 0) then
    lPos := Length(ADateTimeStr);
  ADateStr := Copy(ADateTimeStr, 1, lPos);
  ATimeStr := Trim(Copy(ADateTimeStr, lPos + 1, 100));
  if (Length(ATimeStr) <> 0) then
    Result := 2
  else
  begin
    Result := 1; // found 1 string
    // 2 cases when ADateTimeStr only contains a time:
    // Date/time separator differ, and string contains a timeseparator
    // Date/time separators are equal, but transformation to date fails.
    if ((AFormatSettings.DateSeparator <> AFormatSettings.TimeSeparator) and (Pos(AFormatSettings.TimeSeparator, ADateStr) > 0))
      or ((AFormatSettings.DateSeparator = AFormatSettings.TimeSeparator) and (not TryStrToDate(ADateStr, lDummyDT, AFormatSettings))) then
    begin
      ATimeStr := ADateStr;
      ADateStr := '';
    end;
  end;
end;

function StrToDateTime(const AString: string): TDateTime;
begin
  Result := StrToDateTime(AString, FormatSettings);
end;

function StrToDateTime(const AString: string; const AFormatSettings: TFormatSettings): TDateTime;
var
  lTimeStr, lDateStr: string;
  lPartsFound: Integer;
begin
  lPartsFound := SplitDateTimeStr(AString, lDateStr, lTimeStr, AFormatSettings);
  case lPartsFound of
    0: Result := StrToDate('', AFormatSettings);
    1: if lDateStr <> '' then
         Result := StrToDate(lDateStr, AFormatSettings)
       else
         Result := StrToTime(lTimeStr, AFormatSettings);
    2: Result := ComposeDateTime(StrToDate(lDateStr, AFormatSettings), StrToTime(lTimeStr, AFormatSettings));
  end;
end;

function TryStrToDateTime(const AString: string; out AValue: TDateTime): Boolean;
begin
  Result := TryStrToDateTime(AString, AValue, FormatSettings);
end;

function TryStrToDateTime(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean;
var
  lCount: Integer;
  lDate: TDateTime;
  lTime: TDateTime;
begin
  Result := False;
  lCount := Pos(AFormatSettings.TimeSeparator, AString);
  if lCount > 0 then
  begin
    while (lCount > 0) and (AString[lCount] <> ' ') do
      Dec(lCount);
    if lCount > 0 then
    begin
      if not TryStrToDate(Copy(AString, 1, lCount - 1), lDate, AFormatSettings) then
        Exit;
      if not TryStrToTime(Copy(AString, lCount + 1, Length(AString) - lCount), lTime, AFormatSettings) then
        Exit;
      AValue := ComposeDateTime(lDate, lTime);
      Result := True;
    end
    else
       Result := TryStrToTime(AString, AValue, AFormatSettings);
  end
  else
    Result := TryStrToDate(AString, AValue, AFormatSettings);
end;

function IntStrToTime(out AErrorMsg: string; const AValue: string; ALen: Integer; const AFormatSettings: TFormatSettings): TDateTime;
const
  AMPM_None = 0;
  AMPM_AM = 1;
  AMPM_PM = 2;
  tiHour = 0;
  tiMin = 1;
  tiSec = 2;
  tiMSec = 3;

type
  TTimeValues = array of Word;

var
  lAmPm: Integer;
  lTimeValues: TTimeValues;
  lSeparator: Char;

  function SplitElements(out ATimeValues: TTimeValues; out AAmPm: Integer): Boolean;
  // Strict version. It does not allow #32 as separator, it will treat it as whitespace always
  const
    Digits = '0123456789';
  var
    lCur, lOffset, lElemLen, lErr, lTimeIndex, lFirstSignificantDigit: Integer;
    lValue: Integer;
    lDigitPending, lMSecPending: Boolean;
    lAmPmStr: string;
    lCurChar: Char;
    lCount: Integer;
    lAllowedChars: string;

  begin
    Result := False;
    AAmPm := AMPM_None; // No Am or PM in string found yet
    lMSecPending := False;
    lTimeIndex := 0; // indicating which TTimeValue must be filled next
    for lCount := tiHour to tiMSec do
      ATimeValues[lCount] := 0;
    lCur := 1;
    // skip leading blanks
    while (lCur < ALen) and (AValue[lCur] = #32) do
      Inc(lCur);
    lOffset := lCur;
    // First non-blank cannot be lSeparator or DecimalSeparator
    if (lCur > ALen - 1) or (AValue[lCur] = lSeparator) or (AValue[lCur] = AFormatSettings.Decimalseparator) then
    begin
      // Writeln('Error in sep S[Cur]',AValue[lCur],' ',lSeparator,' ',GetDecimalSeparator);
      Exit;
    end;
    lDigitPending := (Pos(AValue[lCur], Digits) > 0);
    while (lCur <= ALen) do
    begin
      // writeln;
      // writeln('Main While loop:  Cur = ',lCur,' S[Cur] = "',AValue[lCur],'" Len = ',ALen,' separator : ', lSeparator);
      lCurChar := AValue[lCur];
      if Pos(lCurChar, Digits) > 0 then
      begin // Digits
        // HH, MM, SS, or Msec?
        // writeln('Digit: ', lCurChar);
        // Digits are only allowed after starting Am/PM or at beginning of string or after ASeparator
        // and lTimeIndex must be <= tiMSec
        // Uncomment "or (#32 = Separator)" and it will allllow #32 as Separator
        if (not(lDigitPending { or (#32 = lSeparator) } )) or (lTimeIndex > tiMSec) then
        begin
          // Writeln('DigitPending',lElemLen);
          Exit;
        end;
        lOffset := lCur;
        if (lCurChar <> '0') then
          lFirstSignificantDigit := lOffset
        else
          lFirstSignificantDigit := -1;
        while (lCur < ALen) and (Pos(AValue[lCur + 1], Digits) > 0) do
        begin
          // Mark first Digit that is not '0'
          if (lFirstSignificantDigit = -1) and (AValue[lCur] <> '0') then
            lFirstSignificantDigit := lCur;
          Inc(lCur);
        end;
        if (lFirstSignificantDigit = -1) then
          lFirstSignificantDigit := lCur;
        lElemLen := 1 + lCur - lFirstSignificantDigit;
        // writeln('  S[FirstSignificantDigit] = ',AValue[lFirstSignificantDigit], ' S[Cur] = ',AValue[lCur],' ElemLen = ',lElemLen,' -> ', AValue[lOffset], lElemLen);
        // writeln('  Cur = ',lCur);
        // this way we know that Val() will never overflow lValue !
        if (lElemLen <= 2) or ((lElemLen <= 3) and (lTimeIndex = tiMSec)) then
        begin
          Val(Copy(AValue, lFirstSignificantDigit, lElemLen), lValue, lErr);
          // writeln('  Value = ',lValue,' HH = ',ATimeValues[0],' MM = ',ATimeValues[1],' SS = ',ATimeValues[2],' MSec = ',ATimeValues[3]);
          // This is safe now, because we know lValue < High(Word)
          ATimeValues[lTimeIndex] := lValue;
          Inc(lTimeIndex);
          lDigitPending := False;
        end
        else
        begin
          // Writeln('Wrong elemlen: ',lElemLen, ' timeIndex: ',lTimeIndex);
          Exit; // lValue to big, so it must be a wrong timestring
        end;
      end // Digits
      else if (lCurChar = #32) then
      begin
        // writeln('#32');
        // just skip, but we must adress this, or it will be parsed by either AM/PM or lSeparator
      end
      else if (lCurChar = lSeparator) then
      begin
        // writeln('Separator ',lSeparator);
        if lDigitPending or (lTimeIndex > tiSec) then
        begin
          // Writeln ('DigitPending ',lDigitPending,' or (TimeIndex',lTimeIndex,' > tiSec,', tiSec,')');
          Exit;
        end;
        lDigitPending := True;
        lMSecPending := False;
      end
      else if (lCurChar = AFormatSettings.DecimalSeparator) then
      begin
        // writeln('AFormatSettings.DecimalSeparator');
        if lDigitPending or lMSecPending or (lTimeIndex <> tiMSec) then
        begin
          // Writeln('DigitPending ',lDigitPending,' or MSecPending ',lMSecPending,' (',lTimeIndex,',Timeindex, >', tiMSec,'  tiSec)');
          Exit;
        end;
        lDigitPending := True;
        lMSecPending := True;
      end
      else
      begin // AM/PM?
        // None of the above, so this Char _must_ be the start of AM/PM string
        // If we already have found AM/PM or we expect a digit then then timestring must be wrong at this point
        // writeln('AM/PM?');
        if (AAmPm <> AMPM_None) or lDigitPending then
        begin
          // Writeln('AmPm <> AMPM_None) or DigitPending');
          Exit;
        end;
        lOffset := lCur;
        lAllowedChars := AFormatSettings.DecimalSeparator + ' ';
        if lSeparator <> #0 then
          lAllowedChars := lAllowedChars + lSeparator;
        while (lCur < ALen) and (Pos(AValue[lCur + 1], lAllowedChars) = 0) and
          (Pos(AValue[lCur + 1], Digits) = 0) do
          Inc(lCur);
        lElemLen := 1 + lCur - lOffset;
        // writeln('  S[Offset] = ',AValue[1+lOffset], ' S[Cur] = ',AValue[lCur],' ElemLen = ',lElemLen,' -> ', AValue[1+lOffset], lElemLen);
        // writeln('  Cur = ',lCur, 'S =',AValue);
        lAmPmStr := Copy(AValue, lOffset, lElemLen);

        // writeln('AmPmStr = ',lAmPmStr,' (',length(lAmPmStr),')');
        // We must compare to TimeAMString before hardcoded 'AM' for delphi compatibility
        // Also it is perfectly legal, though insane to have TimeAMString = 'PM' and vice versa
        if (CompareText(lAmPmStr, AFormatSettings.TimeAMString) = 0) then
          AAmPm := AMPM_AM
        else if (CompareText(lAmPmStr, AFormatSettings.TimePMString) = 0) then
          AAmPm := AMPM_PM
        else if (CompareText(lAmPmStr, 'AM') = 0) then
          AAmPm := AMPM_AM
        else if (CompareText(lAmPmStr, 'PM') = 0) then
          AAmPm := AMPM_PM
        else
        begin
          // Writeln('No timestring ',lAmPmStr);
          Exit; // If text does not match any of these, timestring must be wrong;
        end;
        // if AM/PM is at beginning of string, then a digit is mandatory after it
        if (lTimeIndex = tiHour) then
          lDigitPending := True
        // otherwise, no more ATimeValues allowed after this
        else
        begin
          lTimeIndex := tiMSec + 1;
          lDigitPending := False;
        end;
      end; // AM/PM
      Inc(lCur)
    end; // while

    // If we arrive here, parsing the elements has been successfull
    // if not at least Hours specified then input is not valid
    // when am/pm is specified Hour must be <= 12 and not 0
    if (lTimeIndex = tiHour) or
      ((AAmPm <> AMPM_None) and ((ATimeValues[tiHour] > 12) or
      (ATimeValues[tiHour] = 0))) or lDigitPending then
      Exit;
    Result := True;
  end;

begin
  SetLength(lTimeValues, 4);
  lSeparator := AFormatSettings.TimeSeparator;
  if lSeparator = #0 then
    lSeparator := ':';
  lAmPm := AMPM_None;
  if not SplitElements(lTimeValues, lAmPm) then
  begin
    AErrorMsg := Format(SErrInvalidTimeFormat, [AValue]);
    Exit;
  end;
  if (lAmPm = AMPM_PM) and (lTimeValues[tiHour] <> 12) then
    Inc(lTimeValues[tiHour], 12)
  else if (lAmPm = AMPM_AM) and (lTimeValues[tiHour] = 12) then
    lTimeValues[tiHour] := 0;
  // Writeln(      lTimeValues[tiHour], lTimeValues[tiMin], lTimeValues[tiSec], lTimeValues[tiMSec]);
  if not TryEncodeTime(lTimeValues[tiHour], lTimeValues[tiMin], lTimeValues[tiSec],
    lTimeValues[tiMSec], Result) then

    AErrorMsg := Format(SErrInvalidTimeFormat, [AValue]);
end;

function TryStrToFloat(const AString: string; out AValue: Extended): Boolean;
begin
  Result := TryStrToFloat(AString, Double(AValue), FormatSettings);
end;

function TryStrToFloat(const AString: string; out AValue: Extended; const AFormatSettings: TFormatSettings): Boolean;
begin
  Result := TryStrToFloat(AString, Double(AValue), AFormatSettings);
end;

function TryStrToFloat(const AString: string; out AValue: Double): Boolean;
begin
  Result := TryStrToFloat(AString, AValue, FormatSettings);
end;

function TryStrToFloat(const AString: string; out AValue: Double; const AFormatSettings: TFormatSettings): Boolean;
const
  cDecSepEN = '.';
var
  lString: string;
  lValue: JSValue;
begin
  lString := AString;
  if AFormatSettings.ThousandSeparator <> '' then
    lString := StringReplace(lString, AFormatSettings.ThousandSeparator, '', [rfReplaceAll]);
  if (AFormatSettings.DecimalSeparator <> cDecSepEN) then
    lString := StringReplace(lString, AFormatSettings.DecimalSeparator, cDecSepEN, []);
  lValue := parseFloat(lString);
  Result := not jsIsNaN(lValue);
  if Result then
    AValue := Double(lValue)
  else
    AValue := 0;
end;

function TryStrToTime(const AString: string; out AValue: TDateTime): Boolean;
begin
  Result := TryStrToTime(AString, AValue, FormatSettings);
end;

function TryStrToTime(const AString: string; out AValue: TDateTime; const AFormatSettings: TFormatSettings): Boolean;
var
  lMsg: string;
begin
  Result := AString <> '';
  if Result then
  begin
    AValue := IntStrToTime(lMsg, AString, Length(AString), AFormatSettings);
    Result := lMsg = '';
  end;
end;

function StrToFloat(const AString: string): Double;
begin
  Result := StrToFloat(AString, FormatSettings);
end;

function StrToFloat(const AString: string; const AFormatSettings: TFormatSettings): Double;
begin
  if not TryStrToFloat(AString, Result, AFormatSettings) then
    raise EConvertError.CreateFmt(SErrInvalidFloat, [AString]);
end;

function StrToTime(const AString: string): TDateTime;
begin
  Result := StrToTime(AString, FormatSettings);
end;

function StrToTime(const AString: string; const AFormatSettings: TFormatSettings): TDateTime;
var
  lMsg: string;
begin
  Result := IntStrToTime(lMsg, AString, Length(AString), AFormatSettings);
  if lMsg <> '' then
    raise EConvertError.Create(lMsg);
end;

function StrToTimeDef(const AString: string; const ADefault: TDateTime): TDateTime;
begin
  Result := StrToTimeDef(AString, ADefault, FormatSettings);
end;

function StrToTimeDef(const AString: string; const ADefault: TDateTime; const AFormatSettings: TFormatSettings): TDateTime;
begin
  if not TryStrToTime(AString, Result, FormatSettings) Then
    Result := ADefault;
end;

procedure UseVariable(const AValue: IInterface);
begin
end;

procedure UseVariable(const AValue: Integer);
begin
end;

procedure UseVariable(const AValue: string);
begin
end;

{ TBytesHelper }

function TBytesHelper.Compare(const ARight: TBytes): Integer;
var
  lCount: Integer;
begin
  if Length > ARight.Length then
    Exit(GreaterThanValue);

  if Length < ARight.Length then
    Exit(LessThanValue);

  Result := EqualsValue;
  for lCount := Low to High do
  begin
    if Self[lCount] > ARight[lCount] then
      Exit(GreaterThanValue);
    if Self[lCount] < ARight[lCount] then
      Exit(LessThanValue);
  end;
end;

function TBytesHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TBytesHelper.High: Integer;
begin
  Result := System.High(Self);
end;

function TBytesHelper.Low: Integer;
begin
  Result := System.Low(Self);
end;

procedure TBytesHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TBytesHelper.SubBytes(const AStartIndex: Integer): TBytes;
begin
  Result := System.Copy(Self, AStartIndex, Length);
end;

function TBytesHelper.SubBytes(const AStartIndex, ALength: Integer): TBytes;
begin
  Result := System.Copy(Self, AStartIndex, ALength);
end;

{ TWordsHelper }

class function TWordsHelper.FromBytes(const ABytes: TBytes): TWords;
const
  cBytesPerWord = 2;
var
  lCount: Integer;
  lLow: Byte;
  lHigh: Byte;
begin
  lCount := ABytes.Length;
  if Odd(lCount) then
    Inc(lCount);
  Result.Length := lCount div cBytesPerWord;

  lCount := ABytes.Low;
  while lCount <= ABytes.High do
  begin
    lLow := ABytes[lCount];
    if lCount + 1 <= ABytes.High then
      lHigh := ABytes[lCount + 1]
    else
      lHigh := 0;

    Result[lCount div cBytesPerWord] := Word.FromBytes(lLow, lHigh);
    Inc(lCount, cBytesPerWord);
  end;

end;

function TWordsHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TWordsHelper.High: Integer;
begin
  Result := System.High(Self);
end;

function TWordsHelper.Low: Integer;
begin
  Result := System.Low(Self);
end;

procedure TWordsHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TWordsHelper.ToBytes: TBytes;
const
  cBytesPerWord = 2;
var
  lLow: Byte;
  lHigh: Byte;
  lCount: Integer;
  lNewIndex: Integer;
begin
  Result.Length := Length * cBytesPerWord;
  for lCount := Low to High do
  begin
    Self[lCount].ToBytes(lLow, lHigh);
    lNewIndex := lCount * cBytesPerWord;
    Result[lNewIndex] := lLow;
    Result[lNewIndex + 1] := lHigh;
  end;
end;

{ TCharHelper }

function TCharHelper.IsDigit: Boolean;
const
  cNumChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
begin
  Result := Self in cNumChars;
end;

function TCharHelper.ToOrd: Integer;
begin
  Result := Ord(Self);
end;

function TCharHelper.ToUpper: Char;
begin
  Result := ToUpper(Self);
end;

class function TCharHelper.ToUpper(const AValue: Char): Char; assembler;
asm
  return AValue.toUpperCase();
end;

{ TIntegerHelper }

function TIntegerHelper.Equals(const AValue: Integer): Boolean;
begin
  Result := Self = AValue;
end;

function TIntegerHelper.NotEquals(const AValue: Integer): Boolean;
begin
  Result := not Equals(AValue);
end;

class function TIntegerHelper.Parse(const AValue: string): Integer;
begin
  Result := StrToInt(AValue);
end;

function TIntegerHelper.ToString: string;
begin
  Result := ToString(Self);
end;

class function TIntegerHelper.ToString(const AValue: Integer): string; assembler;
asm
  return AValue.toString();
end;

class function TIntegerHelper.TryParse(const AString: string; out AValue: Integer): Boolean;
begin
  Result := TryStrToInt(AString, AValue);
end;

{ TLongIntHelper }

function TLongIntHelper.Equals(const AValue: LongInt): Boolean;
begin
  Result := Self = AValue;
end;

function TLongIntHelper.NotEquals(const AValue: LongInt): Boolean;
begin
  Result := not Equals(AValue);
end;

class function TLongIntHelper.Parse(const AValue: string): Integer;
begin
  Result := StrToInt(AValue);
end;

function TLongIntHelper.ToString: string;
begin
  Result := ToString(Self);
end;

class function TLongIntHelper.ToString(const AValue: LongInt): string;
asm
  return AValue.toString();
end;

class function TLongIntHelper.TryParse(const AString: string; out AValue: Integer): Boolean;
begin
  Result := TryStrToInt(AString, AValue);
end;

{ TWordHelper }

class function TWordHelper.FromBytes(const ALow, AHigh: Byte): Word;
const
  cBitPerByte = 8;
begin
  Result := ALow + (AHigh shl cBitPerByte);
end;

procedure TWordHelper.ToBytes(out ALow, AHigh: Byte);
const
  cMask: Byte = $FF;
  cBitPerByte = 8;
begin
  ALow := Self and cMask;
  AHigh := (Self and (cMask shl cBitPerByte)) shr cBitPerByte;
end;

{ TStringHelper }

function TStringHelper.CompareTo(const AValue: string): Integer;
begin
  Result := CompareTo(Self, AValue);
end;

class function TStringHelper.CompareTo(const ALeft, ARight: string): Integer; assembler;
asm
  return ALeft.localeCompare(ARight);
end;

function TStringHelper.Contains(const AValue: string): Boolean;
begin
  Result := IndexOf(AValue) >= 0;
end;

function TStringHelper.Equals(const AValue: string): Boolean;
begin
  Result := Self = AValue;
end;

function TStringHelper.FirstChar: Char;
begin
  if NotIsEmpty then
    Result := Chars[0]
  else
    Result := #0;
end;

function TStringHelper.Format(const AArgs: array of JSValue): string;
begin
  Result := SysUtils.Format(Self, AArgs);
end;

class function TStringHelper.Format(const AValue: string; const AArgs: array of JSValue): string;
begin
  Result := SysUtils.Format(AValue, AArgs);
end;

function TStringHelper.get_Chars(const AIndex: Integer): Char;
begin
  Result := Self[AIndex + 1];
end;

function TStringHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TStringHelper.High: Integer;
begin
  Result := Length - 1;
end;

function TStringHelper.IndexOf(const AValue: Char): Integer;
begin
  Result := Pos(AValue, Self, 1) - 1;
end;

function TStringHelper.IndexOf(const AValue: string; const AStartIndex, ACount: Integer): Integer;
begin
  Result := Pos(AValue, Self, AStartIndex + 1) - 1;
  if (Result + AValue.Length) > (AStartIndex + ACount) then
    Result := -1;
end;

function TStringHelper.IndexOf(const AValue: string): Integer;
begin
  Result := System.Pos(AValue, Self) - 1;
end;

function TStringHelper.Insert(const AStartIndex: Integer; const AValue: string): string;
begin
  System.Insert(AValue, Self, AStartIndex + 1);
  Result := Self;
end;

function TStringHelper.IsEmpty: Boolean;
begin
  Result := Self = Empty;
end;

function TStringHelper.IsNumber: Boolean;
const
  cNumbers = ['0'..'9'];
var
  lCount: Integer;
begin
  if IsEmpty then
    Exit(False);

  for lCount := 0 to Length - 1 do
  begin
    if not (Chars[lCount] in cNumbers) then
      Exit(False);
  end;
  Result := True;
end;

function TStringHelper.LastIndexOf(const AValue: Char): Integer;
begin
  Result := Reverse.IndexOf(AValue);
  if Result >= 0 then
    Result := Length - Result - 1;
end;

class function TStringHelper.Low: Integer;
begin
  Result := 0;
end;

function TStringHelper.NotEquals(const AValue: string): Boolean;
begin
  Result := not Equals(AValue);
end;

function TStringHelper.NotIsEmpty: Boolean;
begin
  Result := Self <> Empty;
end;

function TStringHelper.PadLeft(const ATotalWidth: Integer): string;
const
  cDefPadChar = ' ';
begin
  Result := PadLeft(ATotalWidth, cDefPadChar);
end;

function TStringHelper.PadLeft(ATotalWidth: Integer; const APaddingChar: Char): string;
begin
  ATotalWidth := ATotalWidth - Length;
  if ATotalWidth > 0 then
    Result := System.StringOfChar(APaddingChar, ATotalWidth) + Self
  else
    Result := Self;
end;

function TStringHelper.Pos(const ASubStr, AStr: string; const AOffset: Integer): Integer;
asm
  return AStr.indexOf(ASubStr, AOffset - 1) + 1;
end;

function TStringHelper.Remove(const AStartIndex: Integer): string;
begin
  Result := Remove(AStartIndex, 1);
end;

function TStringHelper.Remove(const AStartIndex, ACount: Integer): string;
begin
  Result := SubString(0, AStartIndex - 1) + SubString(AStartIndex + ACount);
end;

function TStringHelper.Replace(const AOldValue, ANewValue: string): string;
begin
  Result := SysUtils.StringReplace(Self, AOldValue, ANewValue, [rfReplaceAll]);
end;

function TStringHelper.Reverse: string;
var
  lCount: Integer;
begin
  Result := Empty;
  Result.Length := Length;
  for lCount := 0 to Length - 1 do
    Result.Chars[Length - lCount - 1] := Self.Chars[lCount];
end;

function TStringHelper.Same(const AValue: string): Boolean;
begin
  Result := SysUtils.SameText(Self, AValue);
end;

procedure TStringHelper.set_Chars(const AIndex: Integer; const AValue: Char);
begin
  Self[AIndex + 1] := AValue;
end;

procedure TStringHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

class function TStringHelper.StartsText(const ASubText, AText: string): Boolean;
begin
  if ASubText.IsEmpty then
    Result := True
  else
  begin
    if AText.Length >= ASubText.Length then
      Result := SysUtils.SameText(AText.Substring(0, ASubText.Length), ASubText)
    else
      Result := False;
  end;
end;

function TStringHelper.StartsWith(const AValue: string): Boolean;
begin
  Result := StartsWith(AValue, False);
end;

function TStringHelper.StartsWith(const AValue: string; AIgnoreCase: Boolean): Boolean;
begin
  if AIgnoreCase then
    Result := StartsText(AValue, Self)
  else
  if AValue = Empty then
    Result := True
  else
  begin
    if (Self.Length >= AValue.Length) then
      Result := Substring(0, AValue.Length).Equals(AValue)
    else
      Result := False;
  end;
end;

function TStringHelper.Substring(const AStartIndex: Integer): string;
begin
  Result := System.Copy(Self, AStartIndex + 1, Self.Length);
end;

function TStringHelper.Substring(const AStartIndex, ALength: Integer): string;
begin
  Result := System.Copy(Self, AStartIndex + 1, ALength);
end;

function TStringHelper.ToLower: string;
var
  lValue: string;
begin
  lValue := Self;
  asm
    lValue = lValue.toLowerCase();
  end;
  Result := lValue;
end;

function TStringHelper.ToUpper: string;
var
  lValue: string;
begin
  lValue := Self;
  asm
    lValue = lValue.toUpperCase();
  end;
  Result := lValue;
end;

function TStringHelper.Trim: string;
begin
  Result := SysUtils.Trim(Self);
end;

{ TDateTimeHelper }

function TDateTimeHelper.Equals(const AValue: TDateTime): Boolean;
begin
  Result := SameValue(Self, AValue);
end;

function TDateTimeHelper.NotEquals(const AValue: TDateTime): Boolean;
begin
  Result := not SameValue(Self, AValue);
end;

function TDateTimeHelper.ToString: string;
begin
  Result := DateTimeToStr(Self, FormatSettings);
end;

{ TFormatSettings }

class function TFormatSettings.Create: TFormatSettings;
begin
  Result := Create(GetBrowserLocale);
end;

class function TFormatSettings.Create(const ALocale: string): TFormatSettings;
const
  cDateSeps: array of Char = ['/', '.', '-'];
  cTimeSeps: array of Char = [':', '.', '-'];
  cDecSeps: array of Char = [',', '.'];
  cThousandSeps: array of Char = ['.', ','];
var
  lTimeStr: string;
  lCount: Integer;
  lLoc: string;
begin
  lLoc := Uppercase(ALocale);
  if pos('DE-', lLoc) = 1 then
  begin
    Result.LongDayNames[1] := 'Sonntag';
    Result.LongDayNames[2] := 'Montag';
    Result.LongDayNames[3] := 'Dienstag';
    Result.LongDayNames[4] := 'Mittwoch';
    Result.LongDayNames[5] := 'Donnerstag';
    Result.LongDayNames[6] := 'Freitag';
    Result.LongDayNames[7] := 'Samstag';
    Result.ShortDayNames[1] := 'Son';
    Result.ShortDayNames[2] := 'Mon';
    Result.ShortDayNames[3] := 'Die';
    Result.ShortDayNames[4] := 'Mit';
    Result.ShortDayNames[5] := 'Don';
    Result.ShortDayNames[6] := 'Fre';
    Result.ShortDayNames[7] := 'Sam';
    Result.ShortMonthNames[1] := 'Jan';
    Result.ShortMonthNames[2] := 'Feb';
    Result.ShortMonthNames[3] := 'Mär';
    Result.ShortMonthNames[4] := 'Apr';
    Result.ShortMonthNames[5] := 'Mai';
    Result.ShortMonthNames[6] := 'Jun';
    Result.ShortMonthNames[7] := 'Jul';
    Result.ShortMonthNames[8] := 'Aug';
    Result.ShortMonthNames[9] := 'Sep';
    Result.ShortMonthNames[10] := 'Okt';
    Result.ShortMonthNames[11] := 'Nov';
    Result.ShortMonthNames[12] := 'Dez';
    Result.LongMonthNames[1] := 'Januar';
    Result.LongMonthNames[2] := 'Februar';
    Result.LongMonthNames[3] := 'März';
    Result.LongMonthNames[4] := 'April';
    Result.LongMonthNames[5] := 'Mai';
    Result.LongMonthNames[6] := 'Juni';
    Result.LongMonthNames[7] := 'Juli';
    Result.LongMonthNames[8] := 'August';
    Result.LongMonthNames[9] := 'September';
    Result.LongMonthNames[10] := 'Oktober';
    Result.LongMonthNames[11] := 'November';
    Result.LongMonthNames[12] := 'Dezember';
  end
  else
  if pos('FR-', Uppercase(ALocale)) = 1 then
  begin
    Result.LongDayNames[1] := 'Dimanche';
    Result.LongDayNames[2] := 'Lundi';
    Result.LongDayNames[3] := 'Mardi';
    Result.LongDayNames[4] := 'Mecredi';
    Result.LongDayNames[5] := 'Jeudi';
    Result.LongDayNames[6] := 'Vendredi';
    Result.LongDayNames[7] := 'Samedi';
    Result.ShortDayNames[1] := 'Dim';
    Result.ShortDayNames[2] := 'Lun';
    Result.ShortDayNames[3] := 'Mar';
    Result.ShortDayNames[4] := 'Mer';
    Result.ShortDayNames[5] := 'Jeu';
    Result.ShortDayNames[6] := 'Ven';
    Result.ShortDayNames[7] := 'Sam';

    Result.ShortMonthNames[1] := 'Jan';
    Result.ShortMonthNames[2] := 'Fév';
    Result.ShortMonthNames[3] := 'Mar';
    Result.ShortMonthNames[4] := 'Avr';
    Result.ShortMonthNames[5] := 'Mai';
    Result.ShortMonthNames[6] := 'Jun';
    Result.ShortMonthNames[7] := 'Jul';
    Result.ShortMonthNames[8] := 'Aoû';
    Result.ShortMonthNames[9] := 'Sep';
    Result.ShortMonthNames[10] := 'Oct';
    Result.ShortMonthNames[11] := 'Nov';
    Result.ShortMonthNames[12] := 'Dec';
    Result.LongMonthNames[1] := 'Janvier';
    Result.LongMonthNames[2] := 'Février';
    Result.LongMonthNames[3] := 'Mars';
    Result.LongMonthNames[4] := 'Avril';
    Result.LongMonthNames[5] := 'Mai';
    Result.LongMonthNames[6] := 'Juin';
    Result.LongMonthNames[7] := 'Juillet';
    Result.LongMonthNames[8] := 'Août';
    Result.LongMonthNames[9] := 'Septembre';
    Result.LongMonthNames[10] := 'Octobre';
    Result.LongMonthNames[11] := 'Novembre';
    Result.LongMonthNames[12] := 'Décembre';
  end
  else
  if pos('ES-', Uppercase(ALocale)) = 1 then
  begin
    Result.LongDayNames[1] := 'Domingo';
    Result.LongDayNames[2] := 'Lunes';
    Result.LongDayNames[3] := 'Martes';
    Result.LongDayNames[4] := 'Miércoles';
    Result.LongDayNames[5] := 'Jueves';
    Result.LongDayNames[6] := 'Viernes';
    Result.LongDayNames[7] := 'Sábado';
    Result.ShortDayNames[1] := 'Dom';
    Result.ShortDayNames[2] := 'Lun';
    Result.ShortDayNames[3] := 'Mar';
    Result.ShortDayNames[4] := 'Mié';
    Result.ShortDayNames[5] := 'Jue';
    Result.ShortDayNames[6] := 'Vie';
    Result.ShortDayNames[7] := 'Sab';

    Result.ShortMonthNames[1] := 'Ene';
    Result.ShortMonthNames[2] := 'Feb';
    Result.ShortMonthNames[3] := 'Mar';
    Result.ShortMonthNames[4] := 'Abr';
    Result.ShortMonthNames[5] := 'May';
    Result.ShortMonthNames[6] := 'Jun';
    Result.ShortMonthNames[7] := 'Jul';
    Result.ShortMonthNames[8] := 'Ago';
    Result.ShortMonthNames[9] := 'Sep';
    Result.ShortMonthNames[10] := 'Oct';
    Result.ShortMonthNames[11] := 'Nov';
    Result.ShortMonthNames[12] := 'Dic';
    Result.LongMonthNames[1] := 'January';
    Result.LongMonthNames[2] := 'Febrero';
    Result.LongMonthNames[3] := 'Marzi';
    Result.LongMonthNames[4] := 'Abril';
    Result.LongMonthNames[5] := 'Mayo';
    Result.LongMonthNames[6] := 'Junio';
    Result.LongMonthNames[7] := 'Julio';
    Result.LongMonthNames[8] := 'Agusto';
    Result.LongMonthNames[9] := 'Septiembre';
    Result.LongMonthNames[10] := 'Octubre';
    Result.LongMonthNames[11] := 'Noviembre';
    Result.LongMonthNames[12] := 'Diciembre';
  end
  else
  begin
    Result.LongDayNames[1] := 'Sunday';
    Result.LongDayNames[2] := 'Monday';
    Result.LongDayNames[3] := 'Tuesday';
    Result.LongDayNames[4] := 'Wednesday';
    Result.LongDayNames[5] := 'Thursday';
    Result.LongDayNames[6] := 'Friday';
    Result.LongDayNames[7] := 'Saturday';
    Result.ShortDayNames[1] := 'Sun';
    Result.ShortDayNames[2] := 'Mon';
    Result.ShortDayNames[3] := 'Tue';
    Result.ShortDayNames[4] := 'Wed';
    Result.ShortDayNames[5] := 'Thu';
    Result.ShortDayNames[6] := 'Fri';
    Result.ShortDayNames[7] := 'Sat';
    Result.ShortMonthNames[1] := 'Jan';
    Result.ShortMonthNames[2] := 'Feb';
    Result.ShortMonthNames[3] := 'Mar';
    Result.ShortMonthNames[4] := 'Apr';
    Result.ShortMonthNames[5] := 'May';
    Result.ShortMonthNames[6] := 'Jun';
    Result.ShortMonthNames[7] := 'Jul';
    Result.ShortMonthNames[8] := 'Aug';
    Result.ShortMonthNames[9] := 'Sep';
    Result.ShortMonthNames[10] := 'Oct';
    Result.ShortMonthNames[11] := 'Nov';
    Result.ShortMonthNames[12] := 'Dec';
    Result.LongMonthNames[1] := 'January';
    Result.LongMonthNames[2] := 'February';
    Result.LongMonthNames[3] := 'March';
    Result.LongMonthNames[4] := 'April';
    Result.LongMonthNames[5] := 'May';
    Result.LongMonthNames[6] := 'June';
    Result.LongMonthNames[7] := 'July';
    Result.LongMonthNames[8] := 'August';
    Result.LongMonthNames[9] := 'September';
    Result.LongMonthNames[10] := 'October';
    Result.LongMonthNames[11] := 'November';
    Result.LongMonthNames[12] := 'December';
  end;

  Result.DateTimeToStrFormat[False] := 'c';
  Result.DateTimeToStrFormat[True] := 'f';
  Result.DateSeparator := '-';
  Result.TimeSeparator := ':';
  Result.ShortDateFormat := 'yyyy-mm-dd';
  Result.LongDateFormat := 'ddd, yyyy-mm-dd';
  Result.DecimalSeparator := '.';
  Result.ThousandSeparator := ',';
  Result.TimeAMString := 'AM';
  Result.TimePMString := 'PM';
  Result.TwoDigitYearCenturyWindow := 50;

  Result.ShortTimeFormat := GetLocaleShortTimeFormat(ALocale); // 'hh:nn';
  Result.LongTimeFormat := GetLocaleLongTimeFormat(ALocale); // 'hh:nn:ss';
  Result.ShortDateFormat := GetLocaleShortDateFormat(ALocale);

  for lCount := Low(cDateSeps) to High(cDateSeps) do
  begin
    if Result.ShortDateFormat.IndexOf(cDateSeps[lCount]) >= 0 then
      Result.DateSeparator := cDateSeps[lCount];
  end;

  asm
    var lEvent = new Date('Jan 1, 1980 06:07:08 GMT+00:00');
    var lLoc = "";
    if (ALocale == "")
      lLoc = navigator.language;
    else
      lLoc = ALocale;
    lTimeStr = lEvent.toLocaleTimeString(lLoc);
  end;

  for lCount := Low(cTimeSeps) to High(cTimeSeps) do
  begin
    if lTimeStr.IndexOf(cTimeSeps[lCount]) >= 0 then
      Result.TimeSeparator := cTimeSeps[lCount];
  end;

  for lCount := Low(Result.ShortDayNames) to High(Result.ShortDayNames) do
    Result.ShortDayNames[lCount] := GetLocaleShortDayName(lCount, ALocale);
  for lCount := Low(Result.LongDayNames) to High(Result.LongDayNames) do
    Result.LongDayNames[lCount] := GetLocaleLongDayName(lCount, ALocale);

  for lCount := Low(Result.ShortMonthNames) to High(Result.ShortMonthNames) do
    Result.ShortMonthNames[lCount] := GetLocaleShortMonthName(lCount, ALocale);
  for lCount := Low(Result.LongMonthNames) to High(Result.LongMonthNames) do
    Result.LongMonthNames[lCount] := GetLocaleLongMonthName(lCount, ALocale);

  Result.DecimalSeparator := GetLocaleDecimalSeparator(ALocale);

  for lCount := Low(cDecSeps) to High(cDecSeps) do
  begin
    if Result.DecimalSeparator = cDecSeps[lCount] then
    begin
      Result.ThousandSeparator := cThousandSeps[lCount];
      Break;
    end;
  end;
end;

class function TFormatSettings.GetBrowserLocale: string;
asm
  return navigator.language;
end;

class function TFormatSettings.GetLocaleDecimalSeparator(const ALocale: string): string; assembler;
asm
  var lNumber = 1.1;
  lNumber = lNumber.toLocaleString(ALocale).substring(1, 2);
  return lNumber;
end;

class function TFormatSettings.GetLocaleLongDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, 0, 1); // Sunday
  lBaseDate.setDate(lBaseDate.getDate() + ADayOfWeek - 1);
  return lBaseDate.toLocaleDateString(ALocale, { weekday: 'long' });
end;

class function TFormatSettings.GetLocaleLongMonthName(const AMonth: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, AMonth - 1, 1);
  return lBaseDate.toLocaleDateString(ALocale, { month: 'long' });
end;

class function TFormatSettings.GetLocaleLongTimeFormat(ALocale: string): string;
begin
  Result := 'hh:nn:ss';

  if Uppercase(ALocale) = 'EN-US' then
    Result := 'hh:nn:ss AMPM';
end;

class function TFormatSettings.GetLocaleShortDateFormat(ALocale: string): string;
const
  cDeli = '-';
  cShortLength = 2;
var
  lIndex: Integer;
  res: string;
begin
  res := string.Empty;

  ALocale := ALocale.ToLower;

  lIndex := ALocale.IndexOf(cDeli);

  if lIndex >= 0 then
    // ensure the first part is lowercase, second part is uppercase
    ALocale := ALocale.SubString(0, lIndex) + ALocale.SubString(lIndex).ToUpper
  else
  begin
    // when the locale is the main locale specifier only without country
    if ALocale.Length = cShortLength then
      ALocale := ALocale + cDeli + ALocale.ToUpper;
  end;

  asm
    var lFormats = {
      "ar-SA" : "dd/MM/yy",
      "bg-BG" : "dd.M.yyyy",
      "ca-ES" : "dd/MM/yyyy",
      "zh-TW" : "yyyy/M/d",
      "cs-CZ" : "d.M.yyyy",
      "da-DK" : "dd-MM-yyyy",
      "de-DE" : "dd.MM.yyyy",
      "el-GR" : "d/M/yyyy",
      "en-US" : "M/d/yyyy",
      "fi-FI" : "d.M.yyyy",
      "fr-FR" : "dd/MM/yyyy",
      "he-IL" : "dd/MM/yyyy",
      "hu-HU" : "yyyy. MM. dd.",
      "is-IS" : "d.M.yyyy",
      "it-IT" : "dd/MM/yyyy",
      "ja-JP" : "yyyy/MM/dd",
      "ko-KR" : "yyyy-MM-dd",
      "nl-NL" : "d-M-yyyy",
      "nb-NO" : "dd.MM.yyyy",
      "pl-PL" : "yyyy-MM-dd",
      "pt-BR" : "d/M/yyyy",
      "ro-RO" : "dd.MM.yyyy",
      "ru-RU" : "dd.MM.yyyy",
      "hr-HR" : "d.M.yyyy",
      "sk-SK" : "d. M. yyyy",
      "sq-AL" : "yyyy-MM-dd",
      "sv-SE" : "yyyy-MM-dd",
      "th-TH" : "d/M/yyyy",
      "tr-TR" : "dd.MM.yyyy",
      "ur-PK" : "dd/MM/yyyy",
      "id-ID" : "dd/MM/yyyy",
      "uk-UA" : "dd.MM.yyyy",
      "be-BY" : "dd.MM.yyyy",
      "sl-SI" : "d.M.yyyy",
      "et-EE" : "d.MM.yyyy",
      "lv-LV" : "yyyy.MM.dd.",
      "lt-LT" : "yyyy.MM.dd",
      "fa-IR" : "MM/dd/yyyy",
      "vi-VN" : "dd/MM/yyyy",
      "hy-AM" : "dd.MM.yyyy",
      "az-Latn-AZ" : "dd.MM.yyyy",
      "eu-ES" : "yyyy/MM/dd",
      "mk-MK" : "dd.MM.yyyy",
      "af-ZA" : "yyyy/MM/dd",
      "ka-GE" : "dd.MM.yyyy",
      "fo-FO" : "dd-MM-yyyy",
      "hi-IN" : "dd-MM-yyyy",
      "ms-MY" : "dd/MM/yyyy",
      "kk-KZ" : "dd.MM.yyyy",
      "ky-KG" : "dd.MM.yy",
      "sw-KE" : "M/d/yyyy",
      "uz-Latn-UZ" : "dd/MM yyyy",
      "tt-RU" : "dd.MM.yyyy",
      "pa-IN" : "dd-MM-yy",
      "gu-IN" : "dd-MM-yy",
      "ta-IN" : "dd-MM-yyyy",
      "te-IN" : "dd-MM-yy",
      "kn-IN" : "dd-MM-yy",
      "mr-IN" : "dd-MM-yyyy",
      "sa-IN" : "dd-MM-yyyy",
      "mn-MN" : "yy.MM.dd",
      "gl-ES" : "dd/MM/yy",
      "kok-IN" : "dd-MM-yyyy",
      "syr-SY" : "dd/MM/yyyy",
      "dv-MV" : "dd/MM/yy",
      "ar-IQ" : "dd/MM/yyyy",
      "zh-CN" : "yyyy/M/d",
      "de-CH" : "dd.MM.yyyy",
      "en-GB" : "dd/MM/yyyy",
      "es-MX" : "dd/MM/yyyy",
      "fr-BE" : "d/MM/yyyy",
      "it-CH" : "dd.MM.yyyy",
      "nl-BE" : "d/MM/yyyy",
      "nn-NO" : "dd.MM.yyyy",
      "pt-PT" : "dd-MM-yyyy",
      "sr-Latn-CS" : "d.M.yyyy",
      "sv-FI" : "d.M.yyyy",
      "az-Cyrl-AZ" : "dd.MM.yyyy",
      "ms-BN" : "dd/MM/yyyy",
      "uz-Cyrl-UZ" : "dd.MM.yyyy",
      "ar-EG" : "dd/MM/yyyy",
      "zh-HK" : "d/M/yyyy",
      "de-AT" : "dd.MM.yyyy",
      "en-AU" : "d/MM/yyyy",
      "es-ES" : "dd/MM/yyyy",
      "fr-CA" : "yyyy-MM-dd",
      "sr-Cyrl-CS" : "d.M.yyyy",
      "ar-LY" : "dd/MM/yyyy",
      "zh-SG" : "d/M/yyyy",
      "de-LU" : "dd.MM.yyyy",
      "en-CA" : "dd/MM/yyyy",
      "es-GT" : "dd/MM/yyyy",
      "fr-CH" : "dd.MM.yyyy",
      "ar-DZ" : "dd-MM-yyyy",
      "zh-MO" : "d/M/yyyy",
      "de-LI" : "dd.MM.yyyy",
      "en-NZ" : "d/MM/yyyy",
      "es-CR" : "dd/MM/yyyy",
      "fr-LU" : "dd/MM/yyyy",
      "ar-MA" : "dd-MM-yyyy",
      "en-IE" : "dd/MM/yyyy",
      "es-PA" : "MM/dd/yyyy",
      "fr-MC" : "dd/MM/yyyy",
      "ar-TN" : "dd-MM-yyyy",
      "en-ZA" : "yyyy/MM/dd",
      "es-DO" : "dd/MM/yyyy",
      "ar-OM" : "dd/MM/yyyy",
      "en-JM" : "dd/MM/yyyy",
      "es-VE" : "dd/MM/yyyy",
      "ar-YE" : "dd/MM/yyyy",
      "en-029" : "MM/dd/yyyy",
      "es-CO" : "dd/MM/yyyy",
      "ar-SY" : "dd/MM/yyyy",
      "en-BZ" : "dd/MM/yyyy",
      "es-PE" : "dd/MM/yyyy",
      "ar-JO" : "dd/MM/yyyy",
      "en-TT" : "dd/MM/yyyy",
      "es-AR" : "dd/MM/yyyy",
      "ar-LB" : "dd/MM/yyyy",
      "en-ZW" : "M/d/yyyy",
      "es-EC" : "dd/MM/yyyy",
      "ar-KW" : "dd/MM/yyyy",
      "en-PH" : "M/d/yyyy",
      "es-CL" : "dd-MM-yyyy",
      "ar-AE" : "dd/MM/yyyy",
      "es-UY" : "dd/MM/yyyy",
      "ar-BH" : "dd/MM/yyyy",
      "es-PY" : "dd/MM/yyyy",
      "ar-QA" : "dd/MM/yyyy",
      "es-BO" : "dd/MM/yyyy",
      "es-SV" : "dd/MM/yyyy",
      "es-HN" : "dd/MM/yyyy",
      "es-NI" : "dd/MM/yyyy",
      "es-PR" : "dd/MM/yyyy",
      "am-ET" : "d/M/yyyy",
      "tzm-Latn-DZ" : "dd-MM-yyyy",
      "iu-Latn-CA" : "d/MM/yyyy",
      "sma-NO" : "dd.MM.yyyy",
      "mn-Mong-CN" : "yyyy/M/d",
      "gd-GB" : "dd/MM/yyyy",
      "en-MY" : "d/M/yyyy",
      "prs-AF" : "dd/MM/yy",
      "bn-BD" : "dd-MM-yy",
      "wo-SN" : "dd/MM/yyyy",
      "rw-RW" : "M/d/yyyy",
      "qut-GT" : "dd/MM/yyyy",
      "sah-RU" : "MM.dd.yyyy",
      "gsw-FR" : "dd/MM/yyyy",
      "co-FR" : "dd/MM/yyyy",
      "oc-FR" : "dd/MM/yyyy",
      "mi-NZ" : "dd/MM/yyyy",
      "ga-IE" : "dd/MM/yyyy",
      "se-SE" : "yyyy-MM-dd",
      "br-FR" : "dd/MM/yyyy",
      "smn-FI" : "d.M.yyyy",
      "moh-CA" : "M/d/yyyy",
      "arn-CL" : "dd-MM-yyyy",
      "ii-CN" : "yyyy/M/d",
      "dsb-DE" : "d. M. yyyy",
      "ig-NG" : "d/M/yyyy",
      "kl-GL" : "dd-MM-yyyy",
      "lb-LU" : "dd/MM/yyyy",
      "ba-RU" : "dd.MM.yy",
      "nso-ZA" : "yyyy/MM/dd",
      "quz-BO" : "dd/MM/yyyy",
      "yo-NG" : "d/M/yyyy",
      "ha-Latn-NG" : "d/M/yyyy",
      "fil-PH" : "M/d/yyyy",
      "ps-AF" : "dd/MM/yy",
      "fy-NL" : "d-M-yyyy",
      "ne-NP" : "M/d/yyyy",
      "se-NO" : "dd.MM.yyyy",
      "iu-Cans-CA" : "d/M/yyyy",
      "sr-Latn-RS" : "d.M.yyyy",
      "si-LK" : "yyyy-MM-dd",
      "sr-Cyrl-RS" : "d.M.yyyy",
      "lo-LA" : "dd/MM/yyyy",
      "km-KH" : "yyyy-MM-dd",
      "cy-GB" : "dd/MM/yyyy",
      "bo-CN" : "yyyy/M/d",
      "sms-FI" : "d.M.yyyy",
      "as-IN" : "dd-MM-yyyy",
      "ml-IN" : "dd-MM-yy",
      "en-IN" : "dd-MM-yyyy",
      "or-IN" : "dd-MM-yy",
      "bn-IN" : "dd-MM-yy",
      "tk-TM" : "dd.MM.yy",
      "bs-Latn-BA" : "d.M.yyyy",
      "mt-MT" : "dd/MM/yyyy",
      "sr-Cyrl-ME" : "d.M.yyyy",
      "se-FI" : "d.M.yyyy",
      "zu-ZA" : "yyyy/MM/dd",
      "xh-ZA" : "yyyy/MM/dd",
      "tn-ZA" : "yyyy/MM/dd",
      "hsb-DE" : "d. M. yyyy",
      "bs-Cyrl-BA" : "d.M.yyyy",
      "tg-Cyrl-TJ" : "dd.MM.yy",
      "sr-Latn-BA" : "d.M.yyyy",
      "smj-NO" : "dd.MM.yyyy",
      "rm-CH" : "dd/MM/yyyy",
      "smj-SE" : "yyyy-MM-dd",
      "quz-EC" : "dd/MM/yyyy",
      "quz-PE" : "dd/MM/yyyy",
      "hr-BA" : "d.M.yyyy.",
      "sr-Latn-ME" : "d.M.yyyy",
      "sma-SE" : "yyyy-MM-dd",
      "en-SG" : "d/M/yyyy",
      "ug-CN" : "yyyy-M-d",
      "sr-Cyrl-BA" : "d.M.yyyy",
      "es-US" : "M/d/yyyy"};
    res = lFormats[ALocale] || 'dd/MM/yyyy';
  end;

  Result := res;
end;

class function TFormatSettings.GetLocaleShortDayName(const ADayOfWeek: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, 0, 1); // Sunday
  lBaseDate.setDate(lBaseDate.getDate() + ADayOfWeek - 1);

  var lLoc = "";
  if (ALocale == '')
    lLoc = navigator.language
  else
    lLoc = ALocale;

  return lBaseDate.toLocaleDateString(lLoc, { weekday: 'short' });
end;

class function TFormatSettings.GetLocaleShortMonthName(const AMonth: Integer; const ALocale: string): string; assembler;
asm
  var lBaseDate = new Date(2017, AMonth - 1, 1);
  var lLoc = "";
  if (ALocale == '')
    lLoc = navigator.language
  else
    lLoc = ALocale;
  return lBaseDate.toLocaleDateString(lLoc, { month: 'short' });
end;

class function TFormatSettings.GetLocaleShortTimeFormat(
  ALocale: string): string;
begin
  Result := 'hh:nn';

  if Uppercase(ALocale) = 'EN-US' then
    Result := 'h:nn AMPM';
end;

class function TFormatSettings.Invariant: TFormatSettings;
begin
  Result := Create('EN-US');
  Result.CurrencyString := #$00A4;
  Result.CurrencyFormat := 0;
  Result.CurrencyDecimals := 2;
  Result.DateSeparator := '/';
  Result.TimeSeparator := ':';
  Result.ShortDateFormat := 'MM/dd/yyyy';
  Result.LongDateFormat := 'dddd, dd MMMMM yyyy HH:mm:ss';
  Result.TimeAMString := 'AM';
  Result.TimePMString := 'PM';
  Result.ShortTimeFormat := 'HH:mm';
  Result.LongTimeFormat := 'HH:mm:ss';
  Result.ThousandSeparator := ',';
  Result.DecimalSeparator := '.';
  Result.TwoDigitYearCenturyWindow := 50;
  Result.NegCurrFormat := 0;
end;

{ TFormatDateTimeHelper }

class function TFormatDateTimeHelper.FormatDateTime(const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime): string;
var
  lYear: Word;
  lMonth: Word;
  lDay: Word;
  lDayOfWeek: Word;
  lHour: Word;
  lMinute: Word;
  lSecond: Word;
  lMilliSecond: Word;
begin
  Result := '';
  DecodeDateFully(ADateTime, lYear, lMonth, lDay, lDayOfWeek);
  DecodeTime(ADateTime, lHour, lMinute, lSecond, lMilliSecond);
  // Writeln(DateTime,'->',lYear,',', lMonth, ',',lDay, ',',lDayOfWeek,',',lHour, ',',lMinute, ',',lSecond, ',',lMilliSecond);
  if AFormatStr <> '' then
    StoreFormat(Result, lYear, lMonth, lDay, lHour, lMinute, lSecond, lMilliSecond, lDayOfWeek, AFormatStr, AFormatSettings, ADateTime, 0, False)
  else
    StoreFormat(Result, lYear, lMonth, lDay, lHour, lMinute, lSecond, lMilliSecond, lDayOfWeek, 'C', AFormatSettings, ADateTime, 0, False);
end;

class procedure TFormatDateTimeHelper.StoreFormat(var AResult: string; var AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek: Word; const AFormatStr: string; const AFormatSettings: TFormatSettings; const ADateTime: TDateTime; ANesting: Integer; ATimeFlag: Boolean);
const
  cAMPMFormat1 = 'A/P';
  cAMPMFormat2 = 'AMPM';
  cAMPMFormat3 = 'AM/PM';
var
  lToken: Char;
  lLastFormatToken: Char;
  lPrevLastToken: Char;
  lCount: Integer;
  lClock12: Boolean;
  lBuffer: Integer;
  lIsInterval: Boolean;
  lTemp: Integer;
  lFormatCurrent: Integer;
  lFormatEnd: Integer;
begin
  if ANesting > 1 then  // 0 is original string, 1 is included FormatString
    Exit;
  lFormatCurrent := 1;
  lFormatEnd := AFormatStr.Length;
  lClock12 := False;
  lIsInterval := False;
  // look for unquoted 12-hour clock lToken
  lTemp := 1;
  while lTemp <= lFormatEnd do
    begin
    lToken := AFormatStr[lTemp];
    case lToken of
      '''', '"':
      begin
        Inc(lTemp);
        while (lTemp < lFormatEnd) and (AFormatStr[lTemp] <> lToken) do
          Inc(lTemp);
      end;
      'A', 'a':
      begin
        if cAMPMFormat1.Equals(AFormatStr.Substring(lTemp - 1, cAMPMFormat1.Length))
          or cAMPMFormat2.Equals(AFormatStr.Substring(lTemp - 1, cAMPMFormat2.Length))
          or cAMPMFormat3.Equals(AFormatStr.Substring(lTemp - 1, cAMPMFormat3.Length))then
        begin
          lClock12 := True;
          Break;
        end;
      end;
    end;  // case
    Inc(lTemp);
  end ;
  lToken := #255;
  lLastFormatToken := ' ';
  lPrevLastToken := 'H';
  while lFormatCurrent <= lFormatEnd do
  begin
    lToken := AFormatStr[lFormatCurrent];
    lToken := lToken.ToUpper;
   //  Writeln('Treating token at pos ',lFormatCurrent,', : ',lToken,' (',AFormatStr,')');
    lCount := 1;
    lTemp := lFormatCurrent + 1;
    case lToken of
      '''', '"':
      begin
        while (lTemp < lFormatEnd) and (AFormatStr[lTemp] <> lToken) do
          Inc(lTemp);
        Inc(lTemp);
        lCount := lTemp - lFormatCurrent;
        StoreStr(AResult, AFormatStr, lFormatCurrent + 1, lCount - 2);
      end ;
      'A':
      begin
        if cAMPMFormat2.Equals(AFormatStr.Substring(lFormatCurrent - 1, cAMPMFormat2.Length)) then
        begin
          lCount := 4;
          if AHour < 12 then
            StoreString(AResult, AFormatSettings.TimeAMString)
          else
            StoreString(AResult, AFormatSettings.TimePMString);
        end
        else if cAMPMFormat3.Equals(AFormatStr.Substring(lFormatCurrent - 1, cAMPMFormat3.Length)) then
        begin
          lCount := 5;
          if AHour < 12 then
            StoreStr(AResult, AFormatStr, lFormatCurrent, 2)
          else
            StoreStr(AResult, AFormatStr, lFormatCurrent + 3, 2);
        end
        else if cAMPMFormat1.Equals(AFormatStr.Substring(lFormatCurrent - 1, cAMPMFormat1.Length)) then
        begin
          lCount := 3;
          if AHour < 12 then
            StoreStr(AResult, AFormatStr, lFormatCurrent, 1)
          else
            StoreStr(AResult, AFormatStr, lFormatCurrent + 2, 1);
        end
        else
          raise EConvertError.Create('Illegal character in format string');
      end ;
      '/':
      begin
        //  Writeln('Detected date separator');
        StoreString(AResult, AFormatSettings.DateSeparator);
      end;
      ':': StoreString(AResult, AFormatSettings.TimeSeparator);
      ' ', 'C', 'D', 'H', 'M', 'N', 'S', 'T', 'Y', 'Z', 'F' :
      begin
        // Writeln(lFormatCurrent,' Special Token: ',lToken,', Count: ',lCount,', P: ',lTemp);
        while (lTemp <= lFormatEnd) and (AFormatStr.Chars[lTemp - 1].ToUpper = lToken) do
          Inc(lTemp);
        lCount := lTemp - lFormatCurrent;
        // Writeln(lFormatCurrent,' Special Token: ',lToken,', Count: ',lCount,', P: ',lTemp);
        case lToken of
          ' ': StoreStr(AResult, AFormatStr, lFormatCurrent, lCount);
          'Y': begin
            if lCount > 2 then
              StoreInt(AResult, AYear, 4)
            else
              StoreInt(AResult, AYear mod 100, 2);
          end;
          'M': begin
      if lIsInterval and ((lPrevLastToken = 'H') or ATimeFlag) then
        StoreInt(AResult, AMinute + (AHour + Trunc(Abs(ADateTime))*24)*60, 0)
      else
            if (lLastFormatToken = 'H') or ATimeFlag then
            begin
              if lCount = 1 then
                StoreInt(AResult, AMinute, 0)
              else
                StoreInt(AResult, AMinute, 2);
            end
            else
            begin
              case lCount of
                1: StoreInt(AResult, AMonth, 0);
                2: StoreInt(AResult, AMonth, 2);
                3: StoreString(AResult, AFormatSettings.ShortMonthNames[AMonth]);
              else
                StoreString(AResult, AFormatSettings.LongMonthNames[AMonth]);
              end;
            end;
          end;
          'D': begin
            case lCount of
              1: StoreInt(AResult, ADay, 0);
              2: StoreInt(AResult, ADay, 2);
              3: StoreString(AResult, AFormatSettings.ShortDayNames[ADayOfWeek]);
              4: StoreString(AResult, AFormatSettings.LongDayNames[ADayOfWeek]);
              5: StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
            else
              StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
            end ;
          end ;
          'H':
      if lIsInterval then
        StoreInt(AResult, AHour + Trunc(Abs(ADateTime))*24, 0)
      else
      if lClock12 then
            begin
              lBuffer := AHour mod 12;
              if lBuffer=0 then lBuffer:=12;
              if lCount = 1 then
                StoreInt(AResult, lBuffer, 0)
              else
                StoreInt(AResult, lBuffer, 2);
            end
            else begin
              if lCount = 1 then
                StoreInt(AResult, AHour, 0)
              else
                StoreInt(AResult, AHour, 2);
            end;
          'N': if lIsInterval then
           StoreInt(AResult, AMinute + (AHour + Trunc(Abs(ADateTime))*24)*60, 0)
   else
   if lCount = 1 then
                 StoreInt(AResult, AMinute, 0)
               else
                 StoreInt(AResult, AMinute, 2);
          'S': if lIsInterval then
           StoreInt(AResult, ASecond + (AMinute + (AHour + Trunc(Abs(ADateTime))*24)*60)*60, 0)
         else
         if lCount = 1 then
                 StoreInt(AResult, ASecond, 0)
               else
                 StoreInt(AResult, ASecond, 2);
          'Z': if lCount = 1 then
                 StoreInt(AResult, AMilliSecond, 0)
               else
                 StoreInt(AResult, AMilliSecond, 3);
          'T': if lCount = 1 then
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True)
               else
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True);
          'C': begin
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
                 if (AHour <> 0) or (AMinute <> 0) or (ASecond <> 0) then
                   begin
                    StoreString(AResult, ' ');
                    StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True);
                   end;
               end;
          'F': begin
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.ShortDateFormat, AFormatSettings, ADateTime, ANesting + 1, False);
                 StoreString(AResult, ' ');
                 StoreFormat(AResult, AYear, AMonth, ADay, AHour, AMinute, ASecond, AMilliSecond, ADayOfWeek, AFormatSettings.LongTimeFormat, AFormatSettings, ADateTime, ANesting + 1, True);
               end;
        end;
        lPrevLastToken := lLastFormatToken;
        lLastFormatToken := lToken;
      end;
      else
        StoreString(AResult, lToken);
    end;
    Inc(lFormatCurrent, lCount);
  end;
end;

class procedure TFormatDateTimeHelper.StoreInt(var AResult: string; const AValue, ADigits: Integer);
const
  cFillChar = '0';
var
  lBuffer: string;
begin
  lBuffer := AValue.ToString;
  while (lBuffer.Length < ADigits) do
    lBuffer := cFillChar + lBuffer;
  StoreString(AResult, lBuffer);
end;

class procedure TFormatDateTimeHelper.StoreStr(var AResult: string; const AFormatStr: string; const APos, ALength: Integer);
begin
  AResult := AResult + Copy(AFormatStr, APos, ALength);
end;

class procedure TFormatDateTimeHelper.StoreString(var AResult: string; const AString: string);
begin
  AResult := AResult + AString;
end;


{ TTMSEncoding }

function TTMSEncoding.GetByteCount(const AChars: string; const ACharCount: Integer): Integer;
begin
  Result := GetByteCount(AChars.Substring(0, ACharCount));
end;

function TTMSEncoding.GetByteCount(const AChars: string; const ACharIndex, ACharCount: Integer): Integer;
begin
  Result := GetByteCount(AChars.Substring(ACharIndex, ACharCount));
end;

function TTMSEncoding.GetBytes(const AChars: string; const ACharCount: Integer): TBytes;
begin
  Result := GetBytes(AChars.Substring(0, ACharCount));
end;

function TTMSEncoding.GetBytes(const AChars: string; const ACharIndex, ACharCount: Integer): TBytes;
begin
  Result := GetBytes(AChars.Substring(ACharIndex, ACharCount));
end;

function TTMSEncoding.GetString(const ABytes: TBytes; const ACharCount: Integer): string;
begin
  Result := GetString(ABytes.SubBytes(0, ACharCount));
end;

function TTMSEncoding.GetString(const ABytes: TBytes; const ACharIndex, ACharCount: Integer): string;
begin
  Result := GetString(ABytes.SubBytes(ACharIndex, ACharCount));
end;

class function TTMSEncoding.get_ANSI: TTMSEncoding;
begin
  if FANSI = nil then
    FANSI := TANSIEncoding.Create;
  Result := FANSI;
end;

class function TTMSEncoding.get_Default: TTMSEncoding;
begin
  Result := ANSI;
end;

class function TTMSEncoding.get_Unicode: TTMSEncoding;
begin
  if FUnicode = nil then
    FUnicode := TUnicodeEncoding.Create;
  Result := FUnicode;
end;

class function TTMSEncoding.IsStandardEncoding(AEncoding: TTMSEncoding): Boolean;
begin
  Result := (AEncoding <> nil) and ((AEncoding = FANSI) or (AEncoding = FUnicode));
end;

{ TANSIEncoding }

function TANSIEncoding.GetByteCount(const AChars: string): Integer;
begin
  Result := AChars.Length;
end;

function TANSIEncoding.GetBytes(const AChars: string): TBytes;
begin
  Result := BytesOf(AChars);
end;

function TANSIEncoding.GetString(const ABytes: TBytes): string;
begin
  Result := StringOf(ABytes);
end;

{ TUnicodeEncoding }

function TUnicodeEncoding.GetByteCount(const AChars: string): Integer;
begin
  Result := Char.Size * AChars.Length;
end;

function TUnicodeEncoding.GetBytes(const AChars: string): TBytes;
begin
  InternalGetBytes(AChars, Result);
end;

function TUnicodeEncoding.GetString(const ABytes: TBytes): string;
var
  lWords: TWords;
  lBuffer: string;
begin
  lBuffer := string.Empty;
  lWords := TWords.FromBytes(ABytes);
  if lWords <> nil then
  begin
    asm
      for (var lCount = 0; lCount < lWords.length; lCount++)
        lBuffer += string.fromCharCode(lWords[lCount]);
    end;
  end;
  Result := lBuffer;
end;

procedure TUnicodeEncoding.InternalGetBytes(const AChars: string; out ABytes: TBytes);
var
  lWords: TWords;
begin
  lWords := nil;
  asm
    for (var lCount = 0; lCount < AChars.length; lCount++)
      lWords.push(AChars.charCodeAt(lCount));
  end;
  ABytes := lWords.ToBytes;
end;

{ TStreamHelper }

function TStreamHelper.CopyFrom(const ASource: TStream; ACount: Int64): Int64;
const
  MaxBufSize = $F000;
var
  lBufSize: Integer;
  lCount: Integer;
  lBuffer: TBytes;
begin
  if ACount <= 0 then
  begin
    ASource.Position := 0;
    ACount := ASource.Size;
  end;
  Result := ACount;
  if ACount > MaxBufSize then
    lBufSize := MaxBufSize
  else
    lBufSize := ACount;
  lBuffer.Length := lBufSize;
  try
    while ACount <> 0 do
    begin
      if ACount > lBufSize then
        lCount := lBufSize
      else
        lCount := ACount;
      ASource.ReadBuffer(lBuffer, lCount);
      WriteBuffer(lBuffer, lCount);
      Dec(ACount, lCount);
    end;
  finally
    lBuffer.Length := 0;
  end;
end;

{ TTMSStringStream }

constructor TTMSStringStream.Create;
begin
  Create(string.Empty, TTMSEncoding.Default, False);
end;

constructor TTMSStringStream.Create(const AString: string);
begin
  Create(AString, TTMSEncoding.Default, False);
end;

constructor TTMSStringStream.Create(const AString: string; AEncoding: TTMSEncoding; AOwnsEncoding: Boolean);
var
  lBytes: TBytes;
begin
  inherited Create;
  FEncoding := AEncoding;
  FOwnsEncoding := AOwnsEncoding and not TTMSEncoding.IsStandardEncoding(AEncoding);
  lBytes := FEncoding.GetBytes(AString);
  SetPointer(BytesToMemory(lBytes), lBytes.Length);
end;

destructor TTMSStringStream.Destroy;
begin
  if FOwnsEncoding then
    FEncoding.Free;
  inherited Destroy;
end;

function TTMSStringStream.GetDataString: string;
begin
  Result := FEncoding.GetString(Copy(Bytes, 0, Size));
end;

procedure TTMSStringStream.LoadFromFile(const AFileName: string);
const
  cStatusOK = 200;
var
  lStatus: Integer;
  lResponse: string;
begin
  lResponse := string.Empty;
  lStatus := cStatusOK;
  asm
    var lRequest = new XMLHttpRequest();
    lRequest.open("GET", AFileName, false);
    lRequest.send();

    if (lRequest.status == cStatusOK)
      lResponse = lRequest.responseText
    else
      lStatus = lRequest.status;
  end;

  if lStatus <> cStatusOK then
    raise EStringStreamError.CreateFmt(SCannotGetFile, [AFileName, lStatus]);
  WriteString(lResponse);
end;

procedure TTMSStringStream.LoadFromFile(const AFileName: string; const ALoaded: TProc);
begin
  LoadFromFile(AFileName, ALoaded, nil);
end;

procedure TTMSStringStream.LoadFromFile(const AFileName: string; const ALoaded: TProc; const AError: TProcString);
const
  cAbortMethod = 'abort';
  cErrorMethod = 'error';
  cGetMethod = 'GET';
  cLoadMethod = 'load';
begin
  FLoaded := ALoaded;
  FError := AError;
  FFileName := AFileName;
  FRequest := TJSXMLHttpRequest.New;
  FRequest.AddEventListener(cAbortMethod, @OnAbort);
  FRequest.AddEventListener(cErrorMethod, @OnError);
  FRequest.AddEventListener(cLoadMethod, @OnLoad);
  try
    FRequest.Open(cGetMethod, AFileName, True);
    FRequest.Send;
  except
    on E: Exception do
    begin
      if Assigned(AError) then
        AError(E.Message)
      else
        raise;
    end;
  end;
end;

function TTMSStringStream.InternalLoadFromFileP(const AFileName: string): TJSPromise;
begin
  Result := TJSPromise.New(
    procedure(AResolve, AReject : TJSPromiseResolver)
    begin
      LoadFromFile(AFileName,
      procedure()
      begin
        AResolve(True);
      end,
      procedure(const AError: string)
      begin
        AReject(AError);
      end);
    end);
end;

function TTMSStringStream.LoadFromFileP(const AFileName: string): Boolean; async;
begin
  Result := await(Boolean, InternalLoadFromFileP(AFileName));
end;

function TTMSStringStream.OnAbort(AEvent: TEventListenerEvent): Boolean;
begin
  Result := True;
  if Assigned(FError) then
    FError(Format(SLoadFromFileAborted, [FFileName]));
end;

function TTMSStringStream.OnError(AEvent: TEventListenerEvent): Boolean;
begin
  Result := True;
  if Assigned(FError) then
    FError(Format(SErrorLoadFromFile, [FFileName]));
end;

function TTMSStringStream.OnLoad(AEvent: TEventListenerEvent): Boolean;
const
  cStatusOK = 200;
  cStatusLastOK = 208;
var
  lReq: TJSXMLHttpRequest;
begin
  lReq := TJSXMLHttpRequest(AEvent.target);

  if (lReq.Status < cStatusOK) or (lReq.Status > cStatusLastOK) then
  begin
    if Assigned(FError) then
    begin
      FError(Format(SCannotGetFile, [FFileName, FRequest.Status]));
      Exit;
    end
    else
      raise EStringStreamError.CreateFmt(SCannotGetFile, [FFileName, lReq.Status]);
  end;
  WriteString(lReq.ResponseText);
  Result := True;
  if Assigned(FLoaded) then
    FLoaded();
end;

function TTMSStringStream.ReadString(ACount: Integer): string;
var
  lBytes: TBytes;
  lDest: TBytes;
  lCount: Integer;
begin
  if ACount > Size - Position then
    ACount := Size - Position;

  lDest.Length := ACount;
  lBytes := Bytes;
  for lCount := Position to ACount + Position - 1 do
    lDest[lCount - Position] := lBytes[lCount];

  Result := FEncoding.GetString(lDest);
  Position := Position + ACount;
end;

procedure TTMSStringStream.WriteString(const AString: string);
var
  lBytes: TBytes;
  lData: string;
begin
  lBytes := FEncoding.GetBytes(AString);
  Write(lBytes, 0, lBytes.Length);

  lData := DataString;
  if lData <> AString then
    WriteString(''); 
end;

{ TInterfaceList }

constructor TInterfaceList.Create;
begin
  inherited Create;
  FList := TFPList.Create;
end;

destructor TInterfaceList.Destroy;
begin
  Clear;
  FList.Free;
  inherited Destroy;
end;

function TInterfaceList.Add(const AItem: IInterface): Integer;
begin
  Result := FList.Add(nil);
  Items[Result] := AItem;
end;

procedure TInterfaceList.Clear;
var
  lCount: Integer;
begin
  for lCount := 0 to FList.Count - 1 do
    Items[lCount] := nil;
  FList.Clear;
end;

procedure TInterfaceList.Delete(const AIndex: Integer);
begin
  Items[AIndex] := nil;
  FList.Delete(AIndex);
end;

procedure TInterfaceList.Exchange(const AIndex1, AIndex2: Integer);
begin
  FList.Exchange(AIndex1, AIndex2);
end;

function TInterfaceList.Expand: TInterfaceList;
begin
  FList.Expand;
  Result := Self;
end;

function TInterfaceList.First: IInterface;
begin
  Result := Items[0];
end;

function TInterfaceList.GetEnumerator: TInterfaceListEnumerator;
begin
  Result := TInterfaceListEnumerator.Create(Self);
end;

function TInterfaceList.get_Capacity: Integer;
begin
  Result := FList.Capacity;
end;

function TInterfaceList.get_Count: Integer;
begin
  Result := FList.Count;
end;

function TInterfaceList.get_Items(const AIndex: Integer): IInterface;
begin
  Result := IInterface(FList[AIndex]);
end;

function TInterfaceList.IndexOf(const AItem: IInterface): Integer;
begin
  Result := IndexOfItem(AItem, TDirection.FromBeginning);
end;

function TInterfaceList.IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
begin
  Result := FList.IndexOfItem(AItem, ADirection);
end;

procedure TInterfaceList.Insert(const AIndex: Integer; const AItem: IInterface);
begin
  FList.Insert(AIndex, nil);
  Items[AIndex] := AItem;
end;

function TInterfaceList.Last: IInterface;
begin
  Result := Items[Count - 1];
end;

procedure TInterfaceList.Lock;
begin
end;

function TInterfaceList.Remove(const AItem: IInterface): Integer;
begin
  Result := RemoveItem(AItem, TDirection.FromBeginning);
end;

function TInterfaceList.RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
begin
  Result := FList.IndexOfItem(AItem, ADirection);
  if Result > -1 then
    Delete(Result);
end;

procedure TInterfaceList.set_Capacity(const AValue: Integer);
begin
  FList.Capacity := AValue;
end;

procedure TInterfaceList.set_Count(const AValue: Integer);
begin
  FList.Count := AValue;
end;

procedure TInterfaceList.set_Items(const AIndex: Integer; const AItem: IInterface);
begin
  if FList[AIndex] <> nil then
    IInterface(FList[AIndex])._Release;
  FList[AIndex] := AItem;
  if AItem <> nil then
    AItem._AddRef;
end;

function InternalCompare(AItem1, AItem2: JSValue): Integer;
begin
  Result := FCompare(IInterface(AItem1), IInterface(AItem2));
end;

procedure TInterfaceList.Sort(const ACompare: TCompareItems);
begin
  FCompare := ACompare;
  try
    FList.Sort(@InternalCompare);
  finally
    FCompare := nil;
  end;
end;

procedure TInterfaceList.Unlock;
begin
end;

{ TInterfaceListEnumerator }

constructor TInterfaceListEnumerator.Create(const AInterfaceList: TInterfaceList);
begin
  inherited Create;
  FIndex := -1;
  FInterfaceList := AInterfaceList;
end;

function TInterfaceListEnumerator.get_Current: IInterface;
begin
  Result := FInterfaceList[FIndex];
end;

function TInterfaceListEnumerator.MoveNext: Boolean;
begin
  Result := FIndex < FInterfaceList.Count - 1;
  if Result then
    Inc(FIndex);
end;

{ TPath }

class function TPath.Combine(const APath1, APath2: string): string;
begin
  if APath1.NotIsEmpty and (APath1.Chars[APath1.Length - 1] <> PathDelim) then
    Result := APath1 + PathDelim + APath2
  else
    Result := APath1 + APath2;
end;

class function TPath.GetDirectoryName(const AFileName: string): string;
var
  lIndex: Integer;
begin
  lIndex := AFileName.LastIndexOf(PathDelim);
  if lIndex >= 0 then
    Result := AFileName.SubString(0, lIndex)
  else
    Result := AFileName;
end;

class function TPath.GetExtension(const AFileName: string): string;
const
  cSeparator = '.';
var
  lIndex: Integer;
begin
  Result := string.Empty;
  if AFileName.NotIsEmpty then
  begin
    lIndex := AFileName.LastIndexOf(cSeparator);
    if lIndex >= 0 then
      Result := AFileName.SubString(lIndex);
  end;
end;

{ TDirectory }

class function TDirectory.GetCurrentDirectory: string;
begin
  Result := TPath.GetDirectoryName(window.document.location.href);
end;

{ TStringBuilder }

constructor TStringBuilder.Create(const AValue: string);
begin
  inherited Create;
  FData := AValue;
end;

function TStringBuilder.Append(const AValue: Integer): TStringBuilder;
begin
  Result := Append(AValue.ToString);
end;

function TStringBuilder.Append(const AValue: string): TStringBuilder;
begin
  FData := FData + AValue;
  Result := Self;
end;

function TStringBuilder.get_Length: Integer;
begin
  Result := FData.Length;
end;

function TStringBuilder.Insert(const AIndex, AValue: Integer): TStringBuilder;
begin
  Result := Insert(AIndex, AValue.ToString);
end;

function TStringBuilder.Insert(const AIndex: Integer; const AValue: string): TStringBuilder;
begin
  FData.Insert(AIndex, AValue);
  Result := Self;
end;

procedure TStringBuilder.set_Length(const AValue: Integer);
begin
  FData.Length := AValue;
end;

function TStringBuilder.ToString: string;
begin
  Result := FData;
end;


function ReplaceDecimalSep(AValue: string; const ADS: string): string;
var
  lPos: Integer;
begin
  lPos := Pos('.', AValue);
  if lPos > 0 then
    Result := Copy(AValue, 1, lPos - 1) + ADS + Copy(AValue, lPos + 1, Length(AValue) - lPos)
  else
    Result := AValue;
end;

function FormatGeneralFloat(const AValue: Double; APrecision: Integer; const ADS: string): string;
var
  lP: Integer;
  lPE: Integer;
  lQ: Integer;
  lExponent: Integer;
begin
  if (APrecision = -1) or (APrecision > MaxDigits) then
    APrecision := MaxDigits;
  { First convert to scientific format, with correct APrecision }
  Str(AValue: APrecision + 7, Result);
  { Delete leading spaces }
  Result := TrimLeft(Result);
  lP := Pos('.', Result);
  if lP = 0 then
    Exit;
  { Consider removing lExponent }
  lPE := Pos('E', Result);
  if lPE = 0 then
    Exit(ReplaceDecimalSep(Result, ADS));
  { Read lExponent }
  lQ := lPE + 2;
  lExponent := 0;
  while (lQ <= Length(Result)) do
  begin
    lExponent := lExponent * 10 + Ord(Result[lQ]) - Ord('0');
    Inc(lQ);
  end;
  if Result[lPE + 1] = '-' then
    lExponent := -lExponent;
  if (lP + lExponent < lPE) and (lExponent > -6) then
  begin
    { OK to remove lExponent }
    SetLength(Result, lPE - 1); { Trim lExponent }
    if lExponent >= 0 then
    begin
      { Shift point to right }
      for lQ := 0 to lExponent - 1 do
      begin
        Result[lP] := Result[lP + 1];
        Inc(lP);
      end;
      Result[lP] := '.';
      lP := 1;
      if Result[lP] = '-' then
        Inc(lP);
      while (Result[lP] = '0') and (lP < Length(Result)) and
        (Copy(Result, lP + 1, Length(ADS)) <> ADS) do
        { Trim leading zeros; conversion above should not give any, but occasionally does
          because of rounding }
        System.Delete(Result, lP, 1);
    end
    else
    begin
      { Add zeros at start }
      Insert(Copy('00000', 1, -lExponent), Result, lP - 1);
      Result[lP - lExponent] := Result[lP - lExponent - 1];
      { Copy leading digit }
      Result[lP] := '.';
      if lExponent <> -1 then
        Result[lP - lExponent - 1] := '0';
    end;
    { Remove trailing zeros }
    lQ := Length(Result);
    while (lQ > 0) and (Result[lQ] = '0') do
      Dec(lQ);
    if Result[lQ] = '.' then
      Dec(lQ); { Remove trailing decimal point }
    if (lQ = 0) or ((lQ = 1) and (Result[1] = '-')) then
      Result := '0'
    else
      SetLength(Result, lQ);
  end
  else
  begin
    { Need lExponent, but remove superfluous characters }
    { Delete trailing zeros }
    while Result[lPE - 1] = '0' do
    begin
      System.Delete(Result, lPE - 1, 1);
      Dec(lPE);
    end;
    { If number ends in decimal point, remove it }
    if Result[lPE - 1] = ADS then
    begin
      System.Delete(Result, lPE - 1, 1);
      Dec(lPE);
    end;
    { delete superfluous + in lExponent }
    if Result[lPE + 1] = '+' then
      System.Delete(Result, lPE + 1, 1)
    else
      Inc(lPE);
    while Result[lPE + 1] = '0' do
      { Delete leading zeros in lExponent }
      System.Delete(Result, lPE + 1, 1)
  end;
  Result := ReplaceDecimalSep(Result, ADS)
end;

function FormatExponentFloat(AValue: Double; APrecision, ADigits: Integer; ADS: string; const AFormatSettings: TFormatSettings): string;
var
  lPos: Integer;
begin
  ADS := AFormatSettings.DecimalSeparator;
  if (APrecision = -1) or (APrecision > MaxDigits) then
    APrecision := MaxDigits;
  Str(AValue: APrecision + 7, Result);
  { Delete leading spaces }
  while Result[1] = ' ' do
    System.Delete(Result, 1, 1);
  lPos := Pos('E', Result);
  if lPos = 0 then
    Exit(ReplaceDecimalSep(Result, ADS));
  Inc(lPos, 2);
  if ADigits > 4 then
    ADigits := 4;
  ADigits := Length(Result) - lPos - ADigits + 1;
  if ADigits < 0 then
    Insert(copy('0000', 1, -ADigits), Result, lPos)
  else
    while (ADigits > 0) and (Result[lPos] = '0') do
    begin
      System.Delete(Result, lPos, 1);
      if lPos > Length(Result) then
      begin
        System.Delete(Result, lPos - 2, 2);
        Break;
      end;
      Dec(ADigits);
    end;
  Result := ReplaceDecimalSep(Result, ADS);
end;

function FormatFixedFloat(const AValue: Double; ADigits: Integer; ADS: string): string;

begin
  if ADigits = -1 Then
    ADigits := 2
  else if ADigits > 18 then
    ADigits := 18;
  Str(AValue:0:ADigits, Result);
  if (Result <> '') and (Result[1] = ' ') then
    Delete(Result,1,1);
  Result := ReplaceDecimalSep(Result,ADS);
end;

function FormatNumberFloat(const AValue: Double; ADigits: Integer; const ADS, ATS: string): string;
var
  lPos: Integer;
begin
  if ADigits = -1 then
    ADigits := 2
  else if ADigits > MaxDigits then
    ADigits := MaxDigits;
  Str(AValue: 0: ADigits, Result);
  if (Result <> '') and (Result[1] = ' ') then
    Delete(Result, 1, 1);
  lPos := Pos('.', Result);
  if lPos <= 0 then
    lPos := Length(Result) + 1;

  Result := ReplaceDecimalSep(Result, ADS);
  Dec(lPos, 3);
  if (ATS <> '') and (ATS <> #0) then
  begin
    while (lPos > 1) do
    begin
      if (Result[lPos - 1] <> '-') then
        Insert(ATS, Result, lPos);
      Dec(lPos, 3);
    end;
  end;
end;

function RemoveLeadingNegativeSign(var AValue: string; const ADS: string; const AFormatSettings: TFormatSettings): Boolean;
var
  lCount: PtrInt;
  lTS: string;
  lStartPos: PtrInt;
begin
  Result := False;
  lStartPos := 2;
  lTS := AFormatSettings.ThousandSeparator;
  for lCount := lStartPos to Length(AValue) do
  begin
    Result := (AValue[lCount] in ['0', ADS, 'E', '+']) or (AValue[lCount] = lTS);
    if not Result then
      Break;
  end;
  if Result and (AValue[1] = '-') then
    Delete(AValue, 1, 1);
end;


function FormatNumberCurrency(const AValue: Currency; ADigits: Integer; const ADS, ATS: string; const AFormatSettings: TFormatSettings): string;
var
  lNegative: Boolean;
  lPos: Integer;
begin
  if ADigits = -1 then
    ADigits := AFormatSettings.CurrencyDecimals
  else if ADigits > 18 then
    ADigits := 18;
  Str(AValue: 0: ADigits, Result);
  lNegative := Result[1] = '-';
  if lNegative then
    System.Delete(Result, 1, 1);
  lPos := Pos('.', Result);
  if ATS <> '' then
  begin
    if lPos <> 0 then
      Result := ReplaceDecimalSep(Result, ADS)
    else
      lPos := Length(Result) + 1;
    Dec(lPos, 3);
    while (lPos > 1) do
    begin
      Insert(ATS, Result, lPos);
      Dec(lPos, 3);
    end;
  end;
  if lNegative then
    RemoveLeadingNegativeSign(Result, ADS, AFormatSettings);
  if not lNegative then
    case AFormatSettings.CurrencyFormat of
      0:
        Result := AFormatSettings.CurrencyString + Result;
      1:
        Result := Result + AFormatSettings.CurrencyString;
      2:
        Result := AFormatSettings.CurrencyString + ' ' + Result;
      3:
        Result := Result + ' ' + AFormatSettings.CurrencyString;
    end
  else
    case AFormatSettings.NegCurrFormat of
      0:
        Result := '(' + AFormatSettings.CurrencyString + Result + ')';
      1:
        Result := '-' + AFormatSettings.CurrencyString + Result;
      2:
        Result := AFormatSettings.CurrencyString + '-' + Result;
      3:
        Result := AFormatSettings.CurrencyString + Result + '-';
      4:
        Result := '(' + Result + AFormatSettings.CurrencyString + ')';
      5:
        Result := '-' + Result + AFormatSettings.CurrencyString;
      6:
        Result := Result + '-' + AFormatSettings.CurrencyString;
      7:
        Result := Result + AFormatSettings.CurrencyString + '-';
      8:
        Result := '-' + Result + ' ' + AFormatSettings.CurrencyString;
      9:
        Result := '-' + AFormatSettings.CurrencyString + ' ' + Result;
      10:
        Result := Result + ' ' + AFormatSettings.CurrencyString + '-';
      11:
        Result := AFormatSettings.CurrencyString + ' ' + Result + '-';
      12:
        Result := AFormatSettings.CurrencyString + ' ' + '-' + Result;
      13:
        Result := Result + '-' + ' ' + AFormatSettings.CurrencyString;
      14:
        Result := '(' + AFormatSettings.CurrencyString + ' ' + Result + ')';
      15:
        Result := '(' + Result + ' ' + AFormatSettings.CurrencyString + ')';
    end;
end;

function FloatToStrF(const AValue: Double; const AFormat: TFloatFormat; const APrecision, ADigits: Integer): string;
begin
  Result := FloatToStrF(AValue, AFormat, APrecision, ADigits, FormatSettings);
end;

function FloatToStrF(const AValue: Double; const AFormat: TFloatFormat; const APrecision, ADigits: Integer; const AFormatSettings: TFormatSettings): string;
const
  cMinLength = 1;
  cMinus = '-';
var
  lDecimalSeperator: string;
begin
  lDecimalSeperator := AFormatSettings.DecimalSeparator;
  case AFormat of
    ffGeneral: Result := FormatGeneralFloat(AValue, APrecision, lDecimalSeperator);
    ffExponent: Result := FormatExponentFloat(AValue, APrecision, ADigits, lDecimalSeperator, AFormatSettings);
    ffFixed: Result := FormatFixedFloat(AValue, ADigits, lDecimalSeperator);
    ffNumber: Result := FormatNumberFloat(AValue, ADigits, lDecimalSeperator, AFormatSettings.ThousandSeparator);
    ffCurrency: Result := FormatNumberCurrency(AValue, ADigits, lDecimalSeperator, AFormatSettings.ThousandSeparator, AFormatSettings);
  end;
  if (AFormat <> ffCurrency) and (Result.Length > cMinLength) and (Result.FirstChar = cMinus) then
    RemoveLeadingNegativeSign(Result, lDecimalSeperator, AFormatSettings);
end;

function FloatToStr(const AValue: Double): string;
begin
  Result := FloatToStr(AValue, FormatSettings);
end;

function FloatToStr(const AValue: Double; const AFormatSettings: TFormatSettings): string;
begin
  Result := FloatToStrF(AValue, ffGeneral, 15, 0, AFormatSettings);
end;

procedure DoFormatError(const AErrCode : Longint;const AFmt: string);
begin
  //!! must be changed to contain format string...
  case AErrCode of
   feInvalidFormat : raise EConvertError.Createfmt(SInvalidFormat,[AFmt]);
   feMissingArgument : raise EConvertError.Createfmt(SArgumentMissing,[AFmt]);
   feInvalidArgIndex : raise EConvertError.Createfmt(SInvalidArgIndex,[AFmt]);
  end;
end;

function Format(const AFmt: string; const AArgs: array of JSValue): string;
begin
  Result := Format(AFmt, AArgs, FormatSettings);
end;

function Format(const AFmt: string; const AArgs: array of JSValue; const AFormatSettings: TFormatSettings): string;
var
  lChPos, lOldPos, lArgPos, lDoArg, lLen: SizeInt;
  lHs, lToAdd: string;
  lIndex: SizeInt;
  lWidth, lPrec: Longint;
  lLeft: Boolean;
  lFChar: Char;
  lvq: NativeInt;

  {
    ReadFormat reads the format string. It returns the type character in
    uppercase, and sets lIndex, lWidth, lPrec to their correct values,
    or -1 if not set. It sets lLeft to true if lLeft alignment was requested.
    In case of an error, DoFormatError is called.
  }

  function ReadFormat: Char;

  var
    lValue: NativeInt;

    procedure ReadInteger;

    var
      lCode: Integer;
      lArgN: SizeInt;
    begin
      if lValue <> -1 then
        exit; // Was already read.
      lOldPos := lChPos;
      while (lChPos <= lLen) and (AFmt[lChPos] <= '9') and (AFmt[lChPos] >= '0') do
        Inc(lChPos);
      if lChPos > lLen then
        DoFormatError(feInvalidFormat, AFmt);
      if AFmt[lChPos] = '*' then
      begin

        if lIndex = -1 then
          lArgN := lArgPos
        else
        begin
          lArgN := lIndex;
          Inc(lIndex);
        end;

        if (lChPos > lOldPos) or (lArgN > high(AArgs)) then
          DoFormatError(feInvalidFormat, AFmt);

        lArgPos := lArgN + 1;

        if IsNumber(AArgs[lArgN]) and IsInteger(AArgs[lArgN]) then
          lValue := Integer(AArgs[lArgN])
        else
          DoFormatError(feInvalidFormat, AFmt);
        Inc(lChPos);
      end
      else
      begin
        if (lOldPos < lChPos) then
        begin
          Val(Copy(AFmt, lOldPos, lChPos - lOldPos), lValue, lCode);
          // This should never happen !!
          if lCode > 0 then
            DoFormatError(feInvalidFormat, AFmt);
        end
        else
          lValue := -1;
      end;
    end;

    procedure ReadIndex;

    begin
      if AFmt[lChPos] <> ':' then
        ReadInteger
      else
        lValue := 0; // Delphi undocumented behaviour, assume 0, #11099
      if AFmt[lChPos] = ':' then
      begin
        if lValue = -1 then
          DoFormatError(feMissingArgument, AFmt);
        lIndex := lValue;
        lValue := -1;
        Inc(lChPos);
      end;
    end;

    procedure ReadLeft;

    begin
      if AFmt[lChPos] = '-' then
      begin
        lLeft := True;
        Inc(lChPos);
      end
      else
        lLeft := False;
    end;

    procedure ReadWidth;

    begin
      ReadInteger;
      if lValue <> -1 then
      begin
        lWidth := lValue;
        lValue := -1;
      end;
    end;

    procedure ReadPrec;

    begin
      if AFmt[lChPos] = '.' then
      begin
        Inc(lChPos);
        ReadInteger;
        if lValue = -1 then
          lValue := 0;
        lPrec := lValue;
      end;
    end;

  begin
    lIndex := -1;
    lWidth := -1;
    lPrec := -1;
    lValue := -1;
    Inc(lChPos);
    if AFmt[lChPos] = '%' then
    begin
      Result := '%';
      exit; // VP fix
    end;
    ReadIndex;
    ReadLeft;
    ReadWidth;
    ReadPrec;
    Result := Upcase(AFmt[lChPos]);
  end;

  function Checkarg(AT: TJSValueType; err: Boolean): Boolean;
  {
    Check if argument lIndex is of correct type (AT)
    If lIndex=-1, lArgPos is used, and lArgPos is augmented with 1
    lDoArg is set to the argument that must be used.
  }
  begin
    Result := False;
    if lIndex = -1 then
      lDoArg := lArgPos
    else
      lDoArg := lIndex;
    lArgPos := lDoArg + 1;
    if (lDoArg > high(AArgs)) or (GetValueTYpe(AArgs[lDoArg]) <> AT) then
    begin
      if err then
        DoFormatError(feInvalidArgindex, AFmt);
      dec(lArgPos);
      exit;
    end;
    Result := True;
  end;

begin
  Result := '';
  lLen := Length(AFmt);
  lChPos := 1;
  lOldPos := 1;
  lArgPos := 0;
  while lChPos <= lLen do
  begin
    while (lChPos <= lLen) and (AFmt[lChPos] <> '%') do
      Inc(lChPos);
    if lChPos > lOldPos then
      Result := Result + Copy(AFmt, lOldPos, lChPos - lOldPos);
    if lChPos < lLen then
    begin
      lFChar := ReadFormat;
{$IFDEF fmtdebug}
      DumpFormat(lFChar);
{$ENDIF}
      case lFChar of
        'D':
          begin
            Checkarg(jvtinteger, True);
            lToAdd := IntToStr(NativeInt(AArgs[lDoArg]));
            lWidth := Abs(lWidth);
            lIndex := lPrec - Length(lToAdd);
            if lToAdd[1] <> '-' then
              lToAdd := StringOfChar('0', lIndex) + lToAdd
            else
              // + 1 to accomodate for - sign in length !!
              Insert(StringOfChar('0', lIndex + 1), lToAdd, 2);
          end;
        'U':
          begin
            Checkarg(jvtinteger, True);
            if NativeInt(AArgs[lDoArg]) < 0 then
              DoFormatError(feInvalidArgindex, AFmt);
            lToAdd := IntToStr(NativeInt(AArgs[lDoArg]));
            lWidth := Abs(lWidth);
            lIndex := lPrec - Length(lToAdd);
            lToAdd := StringOfChar('0', lIndex) + lToAdd
          end;
        'E':
          begin
            if Checkarg(jvtFloat, False) or Checkarg(jvtinteger, True) then
              lToAdd := FloatToStrF(Double(AArgs[lDoArg]), ffFixed, 9999, lPrec, AFormatSettings);
          end;
        'F':
          begin
            if Checkarg(jvtFloat, False) or Checkarg(jvtinteger, True) then
              lToAdd := FloatToStrF(Double(AArgs[lDoArg]), ffFixed, 9999, lPrec, AFormatSettings);
          end;
        'G':
          begin
            if Checkarg(jvtFloat, False) or Checkarg(jvtinteger, True) then
              lToAdd := FloatToStrF(Double(AArgs[lDoArg]), ffGeneral, lPrec, 3, AFormatSettings);
          end;
        'N':
          begin
            if Checkarg(jvtFloat, False) or Checkarg(jvtinteger, True) then
              lToAdd := FloatToStrF(Double(AArgs[lDoArg]), ffNumber, 9999, lPrec, AFormatSettings);
          end;
        'M':
          begin
            if Checkarg(jvtFloat, False) or Checkarg(jvtinteger, True) then
              lToAdd := FloatToStrF(Double(AArgs[lDoArg]), ffCurrency, 9999, lPrec, AFormatSettings);
          end;
        'S':
          begin
            Checkarg(jvtString, True);
            lHs := string(AArgs[lDoArg]);
            lIndex := Length(lHs);
            if (lPrec <> -1) and (lIndex > lPrec) then
              lIndex := lPrec;
            lToAdd := Copy(lHs, 1, lIndex);
          end;
        'P':
          begin
            Checkarg(jvtinteger, True);
            lToAdd := IntToHex(NativeInt(AArgs[lDoArg]), 31);
          end;
        'X':
          begin
            Checkarg(jvtinteger, True);
            lvq := NativeInt(AArgs[lDoArg]);
            lIndex := 31; // May need to adjust to NativeInt
            if lPrec > lIndex then
              lToAdd := IntToHex(lvq, lIndex)
            else
            begin
              // determine minimum needed number of hex digits.
              lIndex := 1;
              while (NativeInt(1) shl (lIndex * 4) <= lvq) and (lIndex < 16) do
                Inc(lIndex);
              if lIndex > lPrec then
                lPrec := lIndex;
              lToAdd := IntToHex(lvq, lPrec);
            end;
          end;
        '%':
          lToAdd := '%';
      end;
      if lWidth <> -1 then
        if Length(lToAdd) < lWidth then
          if not lLeft then
            lToAdd := StringOfChar(' ', lWidth - Length(lToAdd)) + lToAdd
          else
            lToAdd := lToAdd + StringOfChar(' ', lWidth - Length(lToAdd));
      Result := Result + lToAdd;
    end;
    Inc(lChPos);
    lOldPos := lChPos;
  end;
end;

function FormatFloat(const AFormat: string; const AValue: Double): string;
begin
  Result := FormatFloat(AFormat, AValue, FormatSettings);
end;

function FormatFloat(const AFormat: string; const AValue: Double; const AFormatSettings: TFormatSettings): string;
type
  TPosArray = array of Integer;
const
  cMaxPrecision = 18;  // Extended precision
var
  // Input in usable format
  lExtended: Extended;              // Value as extended.
  lFloatRec: TFloatRec;             // Value as floatrec.
  lSection: string;          // Format can contain 3 sections, semicolon separated: Pos;Neg;Zero. This is the one to use.
  lSectionLength: Integer;   // Length of lSection.
  // Calculated based on lSection. Static during output
  lThousandSep: Boolean;      // Thousands separator detected in format ?
  lIsScientific: Boolean;     // Use Scientific notation ? (lExtended detected in format)
  lDecimalPos: Integer;       // Position of decimal point in pattern.
  lFirstDigit: Integer;       // First actual digit in input (# or 0), relative to decimal point
  lLastDigit: Integer;        // Last required (0) digit, relative to decimal point
  lRequestedDigits: Integer;  // Number of requested digits, # and 0 alike
  lExpSize: Integer;         // Number of digits in exponent
  lAvailable: Integer;        // lAvailable digits in lFloatRec.
  // These change during output loop
  lCurrent: Integer;          // lCurrent digit in lAvailable digits
  lPadZeroes: Integer;        // Difference in requested digits before comma and exponent, needs to be padded with zeroes.
  lDistToDecimal: Integer;    // Place of lCurrent digit, relative to decimal point taking in account lPadZeroes!

  procedure InitVars;
  begin
    lExtended := AValue;
    lSection := '';
    lSectionLength := 0;
    lThousandSep := False;
    lIsScientific := False;
    lDecimalPos := 0;
    lFirstDigit := MaxInt;
    lLastDigit := 0;
    lRequestedDigits := 0;
    lExpSize := 0;
    lAvailable := -1;
  end;

  procedure ToResult(const AChar: Char);
  begin
    Result := Result + AChar;
  end;

  procedure AddToResult(const AStr: string);
  begin
    Result := Result + AStr;
  end;

  procedure WriteDigit(ADigit: Char);
  // Write a digit to result, prepend with decimalseparator or append with 1000 separator
  begin
    if ADigit = #0 then
     Exit;
    // Writeln('WriteDigit: ',ADigit,', DistToDecimal: ',lDistToDecimal);
    Dec(lDistToDecimal);
    // -1 -> we've arrived behind the decimal
    if (lDistToDecimal = -1) then
    begin
      AddToResult(AFormatSettings.DecimalSeparator);
      ToResult(ADigit);
    end
    else
    begin
      // We're still before the decimal.
      ToResult(ADigit);
      if lThousandSep and ((lDistToDecimal mod 3) = 0) and (lDistToDecimal > 1) then
        AddToResult(AFormatSettings.ThousandSeparator);
    end;
  end;

  function GetDigit: Char;
  // Return next digit from available digits.
  // May return #0 if none available.
  // Will return '0' if applicable.
  begin
    // Writeln(' DistToDecimal <= LastDigit : ',lDistToDecimal,' <= ',lLastDigit,' have digit: ',lCurrent<=lAvailable, ' (',lCurrent,')');
    Result := #0;
    if (lCurrent <= lAvailable) then
    begin
      Result := lFloatRec.Digits[lCurrent];
      Inc(lCurrent);
    end
    else if (lDistToDecimal <= lLastDigit) then
      Dec(lDistToDecimal)
    else
      Result := '0';
    // Writeln('GetDigit ->: ',Result);
  end;

  procedure CopyDigit;
  // Copy a digit (#, 0) to the output with the correct value
  begin
    // Writeln('CopyDigit: Padzeroes: ',lPadZeroes,', DistToDecimal: ',lDistToDecimal);
    if (lPadZeroes = 0) then
      WriteDigit(GetDigit) // No shift needed, just copy what is lAvailable.
    else if (lPadZeroes<0) then
    begin
      // We must prepend zeroes
      Inc(lPadZeroes);
      if (lDistToDecimal<=lFirstDigit) then
        WriteDigit('0')
      else
        Dec(lDistToDecimal);
    end
    else
    begin
      // We must append zeroes
      while lPadZeroes > 0 do
      begin
        WriteDigit(GetDigit);
        Dec(lPadZeroes);
      end;
      WriteDigit(GetDigit);
    end;
  end;

  function GetSections(var APosArray: TPosArray): Integer;
  var
    lFloat: Integer;
    lCount: Integer;
    lChar, lQuote: Char;
    lInQuote: Boolean;
  begin
    Result := 1;
    APosArray[1] := -1;
    APosArray[2] := -1;
    APosArray[3] := -1;
    lInQuote := False;
    lQuote := #0;
    lCount := 1;
    lFloat := Length(AFormat);
    while (lCount <= lFloat) do
    begin
      lChar := AFormat[lCount];
      case lChar of
      ';':
        begin
          if not lInQuote then
          begin
            if Result > 3 then
              raise Exception.Create('Invalid float format');
            APosArray[Result]:=lCount+1;
            Inc(Result);
          end;
        end;
      '"','''':
        begin
          if lInQuote then
            lInQuote := lChar <> lQuote
          else
          begin
            lInQuote := True;
            lQuote := lChar;
          end;
        end;
      end;
      Inc(lCount);
      end;
    if APosArray[Result] = -1 then
      APosArray[Result] := lFloat + 1;
  end;

  procedure AnalyzeFormat;
  var
    lCount: Integer;
    lLength: Integer;
    lQuote: Char;
    lChar: Char;
    lInQuote : Boolean;
  begin
    lLength := lSection.Length;
    lCount := 1;
    lInQuote := False;
    lQuote := #0;
    while (lCount <= lLength) do
    begin
      lChar := lSection[lCount];
      if lChar in ['"',''''] then
      begin
        if lInQuote then
          lInQuote := lChar <> lQuote
        else
        begin
          lInQuote := True;
          lQuote := lChar;
        end;
      end
      else if not lInQuote then
      begin
        case lChar of
        '.':
          begin
            if (lDecimalPos = 0) then
              lDecimalPos := lRequestedDigits + 1;
          end;
        ',':
          begin
            lThousandSep := AFormatSettings.ThousandSeparator <> #0;
          end;
        'e', 'E':
          begin
            Inc(lCount);
            if (lCount < lLength) then
            begin
              lChar:=lSection[lCount];
              lIsScientific:=lChar in ['-','+'];
              if lIsScientific then
              begin
                while (lCount < lLength) and (lSection[lCount + 1] = '0') do
                begin
                  Inc(lExpSize);
                  Inc(lCount);
                end;
              end;
              if lExpSize>4 then
                lExpSize:=4;
            end;
          end;
        '#':
          begin
            Inc(lRequestedDigits);
          end;
        '0':
          begin
            if lRequestedDigits < lFirstDigit then
              lFirstDigit := lRequestedDigits + 1;
            Inc(lRequestedDigits);
            lLastDigit := lRequestedDigits + 1;
          end;
        end;
      end;
      Inc(lCount);
    end;
    if lDecimalPos = 0 then
      lDecimalPos := lRequestedDigits + 1;
    // Writeln('LastDigit: ',lDecimalPos,'-',lLastDigit);
    lLastDigit := lDecimalPos - lLastDigit;
    if lLastDigit > 0 then
      lLastDigit := 0;
    // Writeln('FirstDigit: ',lDecimalPos,'-',lFirstDigit);
    lFirstDigit := lDecimalPos - lFirstDigit;
    if lFirstDigit < 0 then
      lFirstDigit := 0;
  end;

  function ValueOutSideScope: Boolean;
  begin
    Result := ((lFloatRec.Exponent >= 18) and (not lIsScientific)) or (lFloatRec.Exponent = $7FF) or (lFloatRec.Exponent = $800);
  end;

  procedure CalcRunVars;
  var
    lDigit: Integer;
    lPrecision: Integer;
  begin
    if lIsScientific then
    begin
      lPrecision := lRequestedDigits;
      lDigit := 9999;
    end
    else
    begin
      lPrecision := cMaxPrecision;
      lDigit := lRequestedDigits - lDecimalPos + 1;
    end;
    lFloatRec := FloatToDecimal(AValue, lPrecision, lDigit);
    // Writeln('Number of digits available : ',Length(lFloatRec.Digits));
    // For lPrecision:=0 to Length(lFloatRec.Digits)-1 do
    //   Writeln(lPrecision,': ',lFloatRec.Digits[lPrecision]);
    lDistToDecimal := lDecimalPos - 1;
    // Writeln('DistToDecimal : ',lDistToDecimal);
    if lIsScientific then
      lPadZeroes := 0 // No padding.
    else
    begin
      lPadZeroes := lFloatRec.Exponent - (lDecimalPos - 1);
      if (lPadZeroes >= 0) then
        lDistToDecimal := lFloatRec.Exponent;
    end;
    // Writeln('PadZeroes : ',lPadZeroes, ', DistToDecimal : ',lDistToDecimal);
    lAvailable := -1;
    while (lAvailable < High(lFloatRec.Digits)) and (lFloatRec.Digits[lAvailable + 1] <> #0) do
      Inc(lAvailable);
    // Writeln('Available: ',lAvailable);
  end;

  function FormatExponent(const ASign: Char; const AExponent: Integer): string;
  begin
    Result := AExponent.ToString;
    Result := StringOfChar('0', lExpSize - Result.Length) + Result;
    if (AExponent < 0) then
      Result := '-' + Result
    else if (AExponent > 0) and (ASign = '+') then
      Result := ASign + Result;
  end;
var
  lCount: Integer;
  lString: Integer;
  lChar: Char;
  lQuote: Char;
  lPosArray: TPosArray;
  lInLiteral: Boolean;
begin
  SetLength(lPosArray, 4);
  Result := '';
  InitVars;
  // What lSection to use ?
  if (lExtended > 0) then
    lString := 1
  else if (lExtended < 0) then
    lString := 2
  else
    lString := 3;
  lPosArray[0] := 0;
  lCount := GetSections(lPosArray);
  if (lCount < lString) or (lPosArray[lString] - lPosArray[lString - 1] = 0) then
    lString := 1;
  // Extract correct lSection
  lSectionLength := lPosArray[lString] - lPosArray[lString - 1] - 1;
  lSection := Copy(AFormat, lPosArray[lString - 1] + 1, lSectionLength);
  SetLength(lSection, lSectionLength);
  // Writeln('Section ',lCount,' : "',lSection,'" ',lSectionLength);
  AnalyzeFormat;
  // Writeln('RequestedDigits: ',lRequestedDigits,', DecimalPos : ',lDecimalPos,', LastDigit: ',lLastDigit,', FirstDigit: ',lFirstDigit);
  CalcRunVars;
  // If we cannot process value using lCurrent settings, fallback
  if (lSectionLength = 0) or ValueOutSideScope then
  begin
    asm
      lSection = lExtended.toPrecision(15);
    end;
    Result := lSection;
  end;
  // Get Started
  lCount := 1;
  lCurrent := 0;
  lQuote := ' ';
  lInLiteral := False;
  if (lFloatRec.Negative) and (lString = 1) then
    ToResult('-');
  while (lCount <= lSectionLength) do
  begin
    lChar := lSection[lCount];
    // Writeln('Analyzing pos ',lCount,': "',lChar,'"');
    if (lChar in ['"', '''']) then
    begin
      if lInLiteral then
        lInLiteral := lChar <> lQuote
      else
      begin
        lInLiteral := True;
        lQuote := lChar;
      end;
    end
    else if lInLiteral then
      ToResult(lChar)
    else
    begin
      case lChar of
      '0', '#':
        begin
          CopyDigit;
        end;
      '.', ',':
        begin
        end; // Do nothing, handled by CopyDigit
      'e', 'E':
        begin
          ToResult(lChar); // Always needed
          Inc(lCount);
          if lCount <= Length(lSection) then
          begin
            lChar := lSection[lCount];
            if (lChar in ['+','-']) then
            begin
              AddToResult(FormatExponent(lChar, lFloatRec.Exponent - lDecimalPos + 1));
              // Skip rest
              while (lCount < lSectionLength) and (lSection[lCount + 1] = '0') do
                Inc(lCount);
            end;
          end;
        end;
      else
        ToResult(lChar);
      end;
    end;
    Inc(lCount);
  end;
end;


{$HINTS OFF}

function StringToBase64(AValue: string): string;
var
  res: string;
begin
  asm
    res = window.btoa(AValue);
  end;
  Result := res;
end;

function Base64ToString(AValue: string): string;
var
  res: string;
begin
  asm
    res = window.atob(AValue);
  end;
  Result := res;
end;

function ArrayBufferToBase64(Buffer: TJSArrayBuffer): string;
begin
  asm
    function _arrayBufferToBase64(buf) {
    var binary = '';
    var bytes = new Uint8Array(buf);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
        binary += String.fromCharCode( bytes[i] );
    }
    return window.btoa(binary);
    }
    Result = _arrayBufferToBase64(Buffer);
  end;
end;

function Base64ToArrayBuffer(Base64: string): TJSArrayBuffer;
begin
  asm
    function _base64ToArrayBuffer(data) {
        var binary_string = window.atob(data);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    }

    Result = _base64ToArrayBuffer(Base64);
  end;
end;

function SizeOf(const AValue: Double): Integer; overload;
const
  cSizeOfDouble = 8;
begin
  Result := cSizeOfDouble;
end;

function SizeOf(const AValue: Integer): Integer; overload;
const
  cSizeOfInteger = 4;
begin
  Result := cSizeOfInteger;
end;

procedure InsertAfter(newNode, referenceNode: TJSElement);
begin
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
end;


initialization
  FormatSettings := TFormatSettings.Create;

end.
